import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { queryHotelData, queryHotelDataStream, submitAIChatFeedback, getAIChatSession, fetchImportPdf, runWorkflow, getWorkflowRun, uploadChatAttachment, CHAT_ATTACHMENT_ACCEPT } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import LodgeicAIIcon from './LodgeicAIIcon';
import ExpandableAIChatTable from './ExpandableAIChatTable';
import ChartRenderer from './ChartRenderer';
import BriefingCardsRenderer from './BriefingCardsRenderer';
import QuestionCard from './QuestionCard';
import EmailPreviewCard from './EmailPreviewCard';
import ScheduledTaskApprovalCard from './ScheduledTaskApprovalCard';
import ResponseBody from './response/ResponseBody';
import { normalizeSignals } from './response/layoutResponse';
import ForecastChangeCard from './ForecastChangeCard';
import WorkflowRunCard from './WorkflowRunCard';
import AssistantContextChip from './AssistantContextChip';
import AnalysisContextChip from './AnalysisContextChip';
import { useAssistantContext } from '../../assistant/context';
import { resolveAssistantTurnScope, PORTFOLIO_RE } from '../../assistant/context/resolveAssistantTurnScope';
import PnLReportTable from './PnLReportTable';
import DataTable from './DataTable';
import RunCard from './RunCard';
import FileViewerModal from '../FileViewerModal';
import AIScheduledTasksManagement from '../Reports/AIScheduledTasksManagement';
import { FinancialAnalystResponse } from '../FinancialAnalyst';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import './AIChat.css';


function isBriefingStreaming(content) {
  if (!content) return false;
  const fenceIdx = content.indexOf('```briefing-cards');
  if (fenceIdx === -1) return false;
  const afterFence = content.slice(fenceIdx + '```briefing-cards'.length);
  return !/(^|\n)\s*```\s*(\n|$)/.test(afterFence);
}

function contentHasDataArtifact(content) {
  const text = String(content || '');
  const prose = text.replace(/```[a-z_]*\s*[\s\S]*?```/gi, '');
  const markdownRows = prose.match(/^\s*\|.*\|\s*$/gm) || [];
  return markdownRows.length >= 3
    || /```(?:chart|pnl_report|forecast_changes|briefing-cards)\b/i.test(text);
}

function adaptStoredWorkflowRun(stored) {
  if (!stored) return null;
  return {
    run: {
      workflowRunId: stored.id,
      workflowRef: stored.workflowRef,
      workflowKey: stored.workflowKey,
      workflowVersion: stored.workflowVersion,
      orgId: stored.orgId,
      locationId: stored.locationId,
      forecastVersionId: stored.scope?.forecastVersionId ?? null,
      asOf: stored.asOf,
      dataSource: stored.config?.dataSource ?? null,
      state: stored.state,
      contextLabel: stored.pageContext?.label ?? null,
    },
    narrative: stored.narrative || '',
    openQuestions: stored.openQuestions || [],
    evidence: stored.evidence || [],
    proposals: stored.proposalSet?.proposals || [],
    artifacts: stored.artifacts || [],
    rejectedProposals: [],
    pageContext: stored.pageContext || null,
  };
}

// Map a status message emitted by the pipeline to the shape of the artifact
// it's about to produce. Used to show a shape-matched placeholder skeleton
// while a tool runs (5–10s of silent wall time otherwise) so the user sees
// "something is coming" instead of only a thinking orb. Returned type aligns
// with ChartPlaceholder's VARIANTS map.
//
// Only commit to a shape for high-confidence signals:
//   • specific P&L tool dispatch → 'pnl'
//   • forecast-mutation tool dispatch → 'table' (forecast proposal card)
// Ambiguous pre-fetch statuses ("Loading financial data…", "Loading
// performance data…", "Classifying question…") are left as null — the
// thinking orb alone covers those; a skeleton there would often flash out
// for prose-only answers.
function artifactTypeFromStatus(status) {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s.includes('forecast p&l') || s === 'loading p&l...') return 'pnl';
  if (s.includes('forecast change') || s.includes('forecast adjustment') ||
      s.includes('forecast version') || s.includes('forecast driver') ||
      s.includes('forecast override') || s.includes('department driver') ||
      s.includes('forecast month') || s.includes('forecast setup') ||
      s.includes('calculating forecast') || s.includes('comparing forecast')) return 'table';
  if (s.includes('database schema')) return 'table';
  return null;
}

// --- Auto-scope: detect property mentions in a user question -----------------
// We build distinctive, collision-free aliases per property so "river lodge"
// in "where will river lodge end the month on total revenue" pins the scope
// to River Lodge & Cabins but "the lodge" alone doesn't pick a random one.
const _AUTOSCOPE_STOPWORDS = new Set([
  'the', 'hotel', 'inn', 'resort', 'at', 'on', 'of', 'and', 'a', 'an',
]);
const _normalizePropertyText = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildPropertyAliasIndex = (locations) => {
  // propId (string) → Set<alias>
  const aliasesByProp = new Map();
  // alias → Set<propId>
  const reverse = new Map();

  for (const loc of locations || []) {
    const propId = loc.id.toString();
    const tokens = _normalizePropertyText(loc.name).split(' ').filter(Boolean);
    const set = new Set();

    for (let len = tokens.length; len >= 1; len--) {
      for (let i = 0; i + len <= tokens.length; i++) {
        const window = tokens.slice(i, i + len);
        const hasContent = window.some(t => !_AUTOSCOPE_STOPWORDS.has(t));
        if (!hasContent) continue;
        if (len === 1) {
          const t = window[0];
          // 4-char non-stopword tokens (e.g. "coho") are distinctive enough.
          // Shorter tokens ("spa", "pub") false-match too easily.
          if (_AUTOSCOPE_STOPWORDS.has(t) || t.length < 4) continue;
        }
        set.add(window.join(' '));
      }
    }

    aliasesByProp.set(propId, set);
    for (const alias of set) {
      if (!reverse.has(alias)) reverse.set(alias, new Set());
      reverse.get(alias).add(propId);
    }
  }

  // Drop ambiguous aliases (e.g. "lodge" matches two different properties).
  for (const [alias, propIds] of reverse) {
    if (propIds.size > 1) {
      for (const propId of propIds) aliasesByProp.get(propId).delete(alias);
    }
  }

  return aliasesByProp;
};

// Returns distinct properties mentioned in `text`, with the longest matched
// alias per property. 0 or >1 results = ambiguous → caller should NOT
// auto-switch scope. Exactly 1 = safe to pin the chip to that property.
const detectMentionedProperties = (text, locations) => {
  if (!text || !locations?.length) return [];
  const message = ' ' + _normalizePropertyText(text) + ' ';
  const aliasesByProp = buildPropertyAliasIndex(locations);
  const results = [];

  for (const [propId, aliasSet] of aliasesByProp) {
    const sorted = [...aliasSet].sort((a, b) => b.length - a.length);
    for (const alias of sorted) {
      if (message.includes(' ' + alias + ' ')) {
        const loc = locations.find(l => l.id.toString() === propId);
        results.push({ id: propId, name: loc?.name || '', matchedPhrase: alias });
        break;
      }
    }
  }

  return results;
};

// Format a message timestamp smartly — historical messages need their date,
// not just the time-of-day (which would look like the message just happened).
//   - today          → "4:34 PM"
//   - yesterday      → "Yesterday, 4:34 PM"
//   - this year      → "Apr 21, 4:34 PM"
//   - previous years → "Apr 21, 2025, 4:34 PM"
//   - null/invalid   → ''
function formatMessageTimestamp(ts) {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return time;
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `Yesterday, ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const dateStr = sameYear
    ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  return `${dateStr}, ${time}`;
}

// The promptKeys the backend recognizes. A promptKey no longer selects an AI
// architecture — every chat turn runs the same agent turn — so this is now
// just "keys the server has extra prompt instructions or a workflow ref for".
// handlePromptClick nulls everything else so an unknown key is never sent;
// library-only keys (topVendors, otbPickup, rate shops, automations, …) carry
// their full prompt text and route exactly like a typed question.
const SERVER_PROMPT_KEYS = new Set([
  'revenuePerformance', 'financialPerformance', 'forecastOTB', 'budgetVariance',
  'laborAnalysis', 'marketAnalysis', 'dailyBriefing', 'forecast_analyze_improve_accuracy',
]);

const CONSTELLATION_PROMPTS = [
  // Market rates — live Google Hotels rate shops
  { key: 'compTonight', label: 'Rate Shop — Tonight', desc: 'Live comp-set rates for tonight plus our OTB position', svgIcon: '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/><path d="M14 8h4M16 6v4"/>' },
  { key: 'compWeekend', label: 'Rate Shop — Weekend', desc: 'Fri–Sat vs the comp set, night by night, with pickup context', svgIcon: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/><path d="M9 15l2 2 4-4"/>' },
  { key: 'softWeekRates', label: 'Soft Nights Diagnosis', desc: 'Find weak nights, rate shop them, and test whether price is the problem', svgIcon: '<path d="M3 17l5-5 4 3 8-8"/><path d="M21 7v4h-4"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/>' },

  // Automations
  { key: 'anomalyEmail', label: 'Daily Anomaly Email', desc: 'Recurring 7am email when bookings spike, wash, or misprice', svgIcon: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 7l10 6 10-6"/><circle cx="19" cy="5" r="3" fill="currentColor" stroke="none"/>' },
  { key: 'anomalyScan', label: 'Anomaly Scan Now', desc: 'One-off sweep for spikes, cancellation clusters, and off-LY pricing', svgIcon: '<circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M8 11h6M11 8v6"/>' },

  // Daily ops
  { key: 'dailyBriefing', label: 'Daily Briefing', desc: 'Yesterday, tonight, MTD pacing, and EOM estimate — all in one read', svgIcon: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>' },
  { key: 'revenuePerformance', label: 'Revenue Performance', desc: 'Occupancy, ADR, RevPAR, and revenue by segment vs last week', svgIcon: '<path d="M3 3v18h18"/><rect x="7" y="10" width="3" height="8" rx="0.5"/><rect x="12" y="6" width="3" height="12" rx="0.5"/><rect x="17" y="3" width="3" height="15" rx="0.5"/>' },

  // Revenue
  { key: 'marketSegmentMTD', label: 'Market Segmentation MTD', desc: 'Month-to-date revenue by market segment', svgIcon: '<circle cx="12" cy="12" r="9"/><path d="M12 3v9h9"/><path d="M12 12L4 16"/>' },
  { key: 'adrOccupancyTrend', label: 'ADR & Occupancy Trend', desc: 'ADR and occupancy together for the last 30 days', svgIcon: '<path d="M3 17l5-5 4 3 8-8"/><path d="M3 21h18"/>' },
  { key: 'dailyRevenue30d', label: 'Daily Revenue (30d)', desc: 'Daily total revenue trend for the last 30 days', svgIcon: '<path d="M3 3v18h18"/><path d="M6 15l4-4 4 4 6-7"/>' },

  // Financial performance
  { key: 'budgetVsLastMonth', label: 'Budget Performance', desc: 'How did we perform vs budget last month?', svgIcon: '<path d="M3 7h18"/><path d="M12 7v13"/><path d="M5 7l-2 6h4z"/><path d="M19 7l-2 6h4z"/>' },
  { key: 'financialPerformance', label: 'Financial Performance', desc: 'Revenue, expenses, and net income vs budget by department', svgIcon: '<path d="M3 3v18h18"/><path d="M7 16l4-8 4 6 5-10"/>' },
  { key: 'budgetVariance', label: 'Variance Deep-Dive', desc: 'Last month\'s statement with the GL detail behind every miss', svgIcon: '<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>' },
  { key: 'gopFlow', label: 'GOP Flow-Through', desc: 'Did cost controls protect margin last month?', svgIcon: '<path d="M3 3v18h18"/><path d="M7 14l3-3 3 2 4-6"/><path d="M17 7h3v3"/>' },

  // Expenses
  { key: 'expenseOutliers', label: 'Expense Outliers', desc: 'Last month\'s expense anomalies vs historicals', svgIcon: '<path d="M3 13h4l2-7 3 11 2-5 2 3 2-2 3 2"/><circle cx="10" cy="6" r="1.5"/>' },
  { key: 'topVendors', label: 'Top Vendors', desc: 'Top 5 vendors by spend last month', svgIcon: '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 13h6M8 17h4"/>' },

  // Forecast / OTB
  { key: 'forecastOTB', label: 'Forecast & OTB', desc: 'On-the-books and projected occupancy for the next 30 days', svgIcon: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>' },
  { key: 'otbPickup', label: 'OTB Pickup', desc: 'Pickup for the next 7 days vs last week\'s snapshot', svgIcon: '<path d="M3 17l6-6 4 4 8-8"/><path d="M13 7h8v8"/>' },
  { key: 'monthEndRoomsPacing', label: 'Month-End Pacing', desc: 'Where rooms revenue lands this month vs budget at current pace', svgIcon: '<path d="M4 21V4h10l-2 4 2 4H4"/><path d="M4 21v-5"/>' },

  // Labor
  { key: 'laborAnalysis', label: 'Labor Analysis', desc: 'HPOR, DPOR, overtime, and payroll as % of revenue', svgIcon: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>' },
  { key: 'overtimeByDept', label: 'Overtime by Department', desc: 'Which department had the highest OT % last month', svgIcon: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l3 2"/><path d="M9 3h6"/>' },
  { key: 'payrollTrend', label: 'Payroll % Trend', desc: 'Payroll as a % of revenue over the last 3 months', svgIcon: '<path d="M3 17l5-5 4 3 8-9"/><circle cx="6" cy="7" r="2"/><circle cx="18" cy="18" r="2"/>' },

  // F&B
  { key: 'fnbDowWeakness', label: 'Weakest F&B Day', desc: 'Which day of the week is softest for F&B revenue', svgIcon: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/><circle cx="12" cy="15" r="1.5" fill="currentColor"/>' },
  { key: 'fnbOutletDow', label: 'F&B Outlets by Day', desc: 'Average revenue by day of week for each F&B outlet', svgIcon: '<rect x="3" y="14" width="3" height="6"/><rect x="8" y="10" width="3" height="10"/><rect x="13" y="7" width="3" height="13"/><rect x="18" y="12" width="3" height="8"/>' },
];



// ---- AI Assistant v2 prompt library: categorize the constellation prompts ----
const PROMPT_CATEGORIES = [
  { id: 'market',    label: 'Market rates', keys: ['compTonight', 'compWeekend', 'softWeekRates'] },
  { id: 'auto',      label: 'Automations',  keys: ['anomalyEmail', 'anomalyScan'] },
  { id: 'daily',     label: 'Daily ops',  keys: ['dailyBriefing', 'revenuePerformance', 'anomalyScan'] },
  { id: 'revenue',   label: 'Revenue',    keys: ['revenuePerformance', 'marketSegmentMTD', 'adrOccupancyTrend', 'dailyRevenue30d'] },
  { id: 'financial', label: 'Financial',  keys: ['budgetVsLastMonth', 'financialPerformance', 'budgetVariance', 'gopFlow'] },
  { id: 'expense',   label: 'Expenses',   keys: ['expenseOutliers', 'topVendors', 'budgetVariance'] },
  { id: 'forecast',  label: 'Forecast',   keys: ['forecastOTB', 'otbPickup', 'monthEndRoomsPacing', 'softWeekRates'] },
  { id: 'labor',     label: 'Labor',      keys: ['laborAnalysis', 'overtimeByDept', 'payrollTrend'] },
  { id: 'fnb',       label: 'F&B',        keys: ['fnbDowWeakness', 'fnbOutletDow'] },
];

// ---- Inline SVGs for the v2 UI ----
const IconChevronDown = (p) => <svg width={p.size || 11} height={p.size || 11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>;
const IconSearch = (p) => <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconX = (p) => <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>;
const IconPaperclip = (p) => <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>;
const IconStop = (p) => <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="2.5"/></svg>;
const IconPlus = (p) => <svg width={p.size || 13} height={p.size || 13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>;
const IconStar = (p) => <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26z"/></svg>;
const IconCheck = (p) => <svg width={p.size || 11} height={p.size || 11} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>;
const IconPin = (p) => <svg width={p.size || 13} height={p.size || 13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconSliders = (p) => <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>;
const IconDatabase = (p) => <svg width={p.size || 11} height={p.size || 11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>;
const IconClock = (p) => <svg width={p.size || 16} height={p.size || 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
const IconShareUp = (p) => <svg width={p.size || 16} height={p.size || 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v13"/><path d="M8 6l4-4 4 4"/><path d="M4 13v7h16v-7"/></svg>;
const IconPencil = (p) => <svg width={p.size || 13} height={p.size || 13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>;
const IconArrowUp = (p) => <svg width={p.size || 16} height={p.size || 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>;
// Lodgeic bars mark — three rising orange bars per the warm-editorial header spec.
const LodgeicBarsLogo = (p) => (
  <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="9" width="4" height="8" rx="1" fill="var(--lodge-orange-400)"/>
    <rect x="7" y="4" width="4" height="13" rx="1" fill="var(--lodge-orange-400)"/>
    <rect x="13" y="1" width="4" height="16" rx="1" fill="var(--lodge-orange-400)"/>
  </svg>
);

// ---- Welcome starter cards ----
// Lead with the workflows, not lookups: live rate shops, GL-level variance
// forensics, and recurring anomaly alerts — the things a GM can't get from a
// dashboard tile.
const AI_V2_WELCOME_CARDS = [
  { key: 'compTonight',    title: 'Tonight vs comp set',  sub: 'Live rate shop + our OTB position',        iconPath: <><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/><path d="M14 8h4M16 6v4"/></> },
  { key: 'compWeekend',    title: 'Weekend rate check',   sub: 'Fri–Sat vs comps, night by night',          iconPath: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/><path d="M9 15l2 2 4-4"/></> },
  { key: 'budgetVariance', title: 'Variance deep-dive',   sub: "Last month's misses with GL detail",        iconPath: <><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></> },
  { key: 'anomalyEmail',   title: 'Daily anomaly email',  sub: 'Get alerted when bookings look unusual',    iconPath: <><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 7l10 6 10-6"/></> },
];

// ---- Popover components ----
function PropertiesPopover({ open, onClose, locations, selectedIds, onToggle, onSelectAll, onClear, onApply, query, setQuery }) {
  if (!open) return null;
  const filtered = (locations || []).filter(l => !query || (l.name || '').toLowerCase().includes(query.toLowerCase()));
  const all = locations || [];
  const allOn = all.length > 0 && selectedIds.size === all.length;
  // Group by region if present, else single group "Properties".
  const groups = filtered.reduce((acc, l) => {
    const k = l.region || l.state || 'Properties';
    (acc[k] ||= []).push(l);
    return acc;
  }, {});
  return (
    <>
      <div className="ai-popover-scrim" onClick={onClose} />
      <div className="ai-popover ai-popover-properties" onClick={(e) => e.stopPropagation()}>
        <div className="ai-popover-head">
          <div className="ai-popover-title">Scope</div>
          <div className="ai-popover-actions">
            <button type="button" className="ai-text-btn" onClick={onSelectAll}>Select all</button>
            <button type="button" className="ai-text-btn" onClick={onClear}>Clear</button>
          </div>
        </div>
        <div className="ai-popover-search">
          <IconSearch />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter properties…" autoFocus />
        </div>
        <div
          className={'ai-prop-row ai-prop-all ' + (allOn ? 'is-on' : '')}
          onClick={() => (allOn ? onClear() : onSelectAll())}
          role="button"
        >
          <span className={'ai-check ' + (allOn ? 'is-on' : '')}>{allOn && <IconCheck />}</span>
          <span className="ai-prop-name">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--lodge-orange-500)" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
            All properties · portfolio
          </span>
          <span className="ai-prop-meta">{all.length}</span>
        </div>
        <div className="ai-popover-scroll">
          {Object.entries(groups).map(([group, props]) => (
            <div key={group} className="ai-prop-group">
              <div className="ai-prop-group-head">
                <span>{group}</span>
              </div>
              {props.map(p => {
                const id = p.id.toString();
                const on = selectedIds.has(id);
                return (
                  <div
                    key={id}
                    className={'ai-prop-row ' + (on ? 'is-on' : '')}
                    onClick={() => onToggle(id)}
                    role="button"
                  >
                    <span className={'ai-check ' + (on ? 'is-on' : '')}>{on && <IconCheck />}</span>
                    <span className="ai-prop-name">{p.name}</span>
                    {p.brand && <span className="ai-prop-meta">{p.brand}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="ai-popover-foot">
          <div className="ai-popover-foot-count">
            {allOn ? `All ${all.length} properties` : `${selectedIds.size} selected`}
          </div>
          <button type="button" className="ai-primary-btn" onClick={onApply}>Apply</button>
        </div>
      </div>
    </>
  );
}

function PromptsPopover({ open, onClose, onPick, highlightPrompts }) {
  const [cat, setCat] = React.useState('all');
  const [q, setQ] = React.useState('');
  if (!open) return null;
  const items = CONSTELLATION_PROMPTS.filter(p =>
    (cat === 'all' || PROMPT_CATEGORIES.find(c => c.id === cat)?.keys.includes(p.key)) &&
    (!q || p.label.toLowerCase().includes(q.toLowerCase()) || p.desc.toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <>
      <div className="ai-popover-scrim" onClick={onClose} />
      <div className="ai-popover ai-popover-prompts" onClick={(e) => e.stopPropagation()}>
        <div className="ai-popover-head">
          <div className="ai-popover-title">Prompt library</div>
        </div>
        <div className="ai-popover-search">
          <IconSearch />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prompts…" autoFocus />
        </div>
        <div className="ai-prompts-tabs">
          <button type="button" className={'ai-prompts-tab ' + (cat === 'all' ? 'is-on' : '')} onClick={() => setCat('all')}>All</button>
          {PROMPT_CATEGORIES.map(c => (
            <button key={c.id} type="button" className={'ai-prompts-tab ' + (cat === c.id ? 'is-on' : '')} onClick={() => setCat(c.id)}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="ai-popover-scroll ai-prompts-list">
          {items.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--lodge-onyx-400)', textAlign: 'center', fontSize: 12 }}>No prompts match.</div>
          ) : items.map(p => {
            const hasPrompt = highlightPrompts?.[p.key];
            return (
              <button
                key={p.key}
                type="button"
                className="ai-prompt-card"
                disabled={!hasPrompt}
                style={{ opacity: hasPrompt ? 1 : 0.5 }}
                onClick={() => { if (hasPrompt) { onPick(highlightPrompts[p.key], p.key); onClose(); } }}
              >
                <div className="ai-prompt-card-head">
                  <div className="ai-prompt-card-title">
                    <IconStar size={11} />
                    {p.label}
                  </div>
                </div>
                <div className="ai-prompt-card-body">{p.desc}</div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ComposerToolsPopover({ open, onClose, canShare, onPrompts, onHistory, onShare, onSettings, onNewChat }) {
  if (!open) return null;
  const choose = (action) => {
    onClose();
    action();
  };
  return (
    <>
      <div className="ai-popover-scrim" onClick={onClose} />
      <div className="ai-popover ai-popover-tools" role="menu" aria-label="Assistant tools">
        <button type="button" role="menuitem" onClick={() => choose(onPrompts)}>
          <IconStar size={13} />
          <span><strong>Prompt library</strong><small>Browse ready-made analyses</small></span>
        </button>
        <div className="ai-tools-divider" />
        <button type="button" role="menuitem" onClick={() => choose(onHistory)}>
          <IconClock size={14} />
          <span><strong>Conversation history</strong></span>
        </button>
        <button type="button" role="menuitem" disabled={!canShare} onClick={() => choose(onShare)}>
          <IconShareUp size={14} />
          <span><strong>Share conversation</strong><small>{canShare ? 'Send a reviewable link' : 'Available after your first message'}</small></span>
        </button>
        <button type="button" role="menuitem" onClick={() => choose(onSettings)}>
          <IconSliders size={14} />
          <span><strong>Assistant settings</strong></span>
        </button>
        <button type="button" role="menuitem" onClick={() => choose(onNewChat)}>
          <IconPencil size={14} />
          <span><strong>New conversation</strong></span>
        </button>
      </div>
    </>
  );
}

function FastModeToggle({ enabled, onChange }) {
  return (
    <button
      type="button"
      className={`ai-fast-toggle ${enabled ? 'is-on' : ''}`}
      aria-pressed={enabled}
      onClick={() => onChange(!enabled)}
      title={enabled ? 'Fast mode on' : 'Turn on Fast mode'}
    >
      <span>Fast</span>
      <span className="ai-fast-toggle-track" aria-hidden="true"><span /></span>
    </button>
  );
}

function SettingsTray({ open, onClose, settings, onChange }) {
  if (!open) return null;
  return createPortal(
    <>
      <div className="ai-tray-scrim" onClick={onClose} />
      <aside className="ai-tray">
        <div className="ai-tray-head">
          <h3>Assistant settings</h3>
          <button type="button" className="ai-icon-btn" onClick={onClose}><IconX size={16} /></button>
        </div>
        <div className="ai-tray-body">
          <section className="ai-tray-section">
            <div className="ai-tray-section-title">Response style</div>
            <div className="ai-seg">
              {[
                { id: 'concise', label: 'Concise' },
                { id: 'balanced', label: 'Balanced' },
                { id: 'detailed', label: 'Detailed' },
              ].map(o => (
                <button key={o.id} type="button" className={'ai-seg-btn ' + (settings.style === o.id ? 'is-on' : '')}
                  onClick={() => onChange('style', o.id)}>{o.label}</button>
              ))}
            </div>
            <p className="ai-tray-hint">Balanced keeps tables and narrative side-by-side.</p>
          </section>

          <section className="ai-tray-section">
            <div className="ai-tray-section-title">Default time window</div>
            <div className="ai-seg">
              {['MTD', 'QTD', 'YTD', 'Last 30d'].map(o => (
                <button key={o} type="button" className={'ai-seg-btn ' + (settings.window === o ? 'is-on' : '')}
                  onClick={() => onChange('window', o)}>{o}</button>
              ))}
            </div>
          </section>

          <section className="ai-tray-section">
            <div className="ai-tray-section-title">Output</div>
            {[
              { id: 'tables',    label: 'Always show tables', desc: 'Include data tables with narrative' },
              { id: 'charts',    label: 'Include chart links', desc: 'Link to relevant Lodgeic charts' },
              { id: 'reasoning', label: 'Show reasoning',     desc: 'Surface model reasoning when available' },
            ].map(o => (
              <div key={o.id} className="ai-switch-row" onClick={() => onChange(o.id, !settings[o.id])}>
                <span>
                  <div className="ai-switch-label">{o.label}</div>
                  <div className="ai-switch-desc">{o.desc}</div>
                </span>
                <button type="button" className={'ai-switch ' + (settings[o.id] ? 'is-on' : '')}
                  onClick={(e) => { e.stopPropagation(); onChange(o.id, !settings[o.id]); }}>
                  <span className="ai-switch-knob" />
                </button>
              </div>
            ))}
          </section>
        </div>
      </aside>
    </>,
    document.body
  );
}

function AutomationsTray({ open, onClose, currentUser }) {
  if (!open) return null;
  return createPortal(
    <>
      <div className="ai-tray-scrim" onClick={onClose} />
      <aside className="ai-tray ai-tray-automations" aria-label="Automations">
        <div className="ai-tray-head">
          <div>
            <h3>Automations</h3>
            <div className="ai-tray-kicker">Recurring reviews, approvals, and run history</div>
          </div>
          <button type="button" className="ai-icon-btn" onClick={onClose} aria-label="Close automations"><IconX size={16} /></button>
        </div>
        <div className="ai-tray-body ai-tray-automations-body">
          <AIScheduledTasksManagement currentUser={currentUser} />
        </div>
      </aside>
    </>,
    document.body
  );
}

function HistoryTray({ open, onClose, sessions, onPick, onDelete, currentThreadId, onNewChat, isLoading }) {
  const [q, setQ] = React.useState('');
  if (!open) return null;
  const filtered = (sessions || []).filter(s =>
    !q || (s.lastMessage || '').toLowerCase().includes(q.toLowerCase()) || (s.hotelName || '').toLowerCase().includes(q.toLowerCase())
  );
  const formatDate = (ts) => {
    try {
      const d = new Date(ts);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      if (sameDay) return 'Today, ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const yest = new Date(now); yest.setDate(now.getDate() - 1);
      if (d.toDateString() === yest.toDateString()) return 'Yesterday';
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };
  return createPortal(
    <>
      <div className="ai-tray-scrim" onClick={onClose} />
      <aside className="ai-tray ai-tray-left">
        <div className="ai-tray-head">
          <h3>History</h3>
          <button type="button" className="ai-icon-btn" onClick={onClose}><IconX size={16} /></button>
        </div>
        <div className="ai-tray-search">
          <IconSearch />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations…" />
        </div>
        <div className="ai-tray-body">
          <button type="button" className="ai-new-chat-btn" onClick={() => { onNewChat(); onClose(); }}>
            <IconPlus />
            New conversation
          </button>
          {isLoading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--lodge-onyx-400)', fontSize: 12 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--lodge-onyx-400)', fontSize: 12 }}>
              {q ? 'No conversations match.' : 'No conversations yet.'}
            </div>
          ) : (
            <>
              <div className="ai-tray-subhead">Recent</div>
              {filtered.map(s => (
                <div
                  key={s.threadId}
                  className={'ai-conv-row ' + (currentThreadId === s.threadId ? 'is-on' : '')}
                  onClick={() => { onPick(s.threadId); onClose(); }}
                  role="button"
                >
                  <div className="ai-conv-title">
                    {s.isShared && (
                      <span
                        title={s.sharedFrom?.userName ? `Shared by ${s.sharedFrom.userName}` : 'Shared with you'}
                        style={{
                          display: 'inline-block',
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                          color: 'var(--lodge-orange-500, #E65F00)',
                          background: 'var(--lodge-orange-50, #FFF4ED)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          marginRight: 6,
                          verticalAlign: 'middle'
                        }}
                      >
                        Shared
                      </span>
                    )}
                    {s.lastMessage || 'Conversation'}
                  </div>
                  {s.hotelName && (
                    <div className="ai-conv-preview">
                      {s.hotelName}
                      {s.isShared && s.sharedFrom?.userName ? ` · from ${s.sharedFrom.userName}` : ''}
                    </div>
                  )}
                  <div className="ai-conv-when">
                    {s.messageCount ? `${s.messageCount} msgs · ` : ''}{formatDate(s.lastUpdated)}
                  </div>
                  {onDelete && (
                    <button
                      type="button"
                      className="ai-conv-delete"
                      onClick={(e) => { e.stopPropagation(); onDelete(s.threadId); }}
                      title="Delete"
                    >
                      <IconX size={12} />
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
}

function WelcomeV2({ onPickPrompt, onOpenPrompts, highlightPrompts, hasLocation }) {
  return (
    <div className="ai-welcome">
      <LodgeicBarsLogo size={34} />
      <h1 className="ai-welcome-title">Where should we start?</h1>
      <p className="ai-welcome-sub">
        {hasLocation
          ? 'Ask about any property, or run a cross-portfolio analysis.'
          : 'Select a property to get started, or ask a question across all your locations.'}
      </p>

      <div className="ai-welcome-grid">
        {AI_V2_WELCOME_CARDS.map((c) => {
          const prompt = highlightPrompts?.[c.key];
          return (
            <button
              key={c.key}
              type="button"
              className="ai-welcome-card"
              disabled={!prompt}
              onClick={() => prompt && onPickPrompt(prompt, c.key)}
            >
              <div className="ai-welcome-card-title">{c.title}</div>
              <div className="ai-welcome-card-sub">{c.sub}</div>
            </button>
          );
        })}
      </div>

      <button type="button" className="ai-welcome-browse" onClick={onOpenPrompts}>
        <IconStar size={12} />
        Browse prompt library
        <IconChevronDown size={11} />
      </button>
    </div>
  );
}


// `contextPrefix` is the LEGACY one-shot prompt preamble, still used by the
// briefing entry point. Page state now travels as assistant_context.v1 on every
// turn, so surfaces on that path pass neither prop — hence the defaults.
const AIChat = ({ selectedHotelId, selectedHotelName, activeThreadId, preserveHistoryOnScopeChange = false, onNewConversation, onSessionUpdated, suggestedPrompt, onSuggestedPromptUsed, contextPrefix = null, onContextPrefixConsumed = null, pendingAction = null, onPendingActionConsumed = null, compact, suggestedPrompts, highlightPrompts, locations, onHotelChange, onScopeRestore = null, chatSessions, isLoadingSessions, onSelectSession, onDeleteSession, newConversationRequest = 0, externalTray = null, onExternalTrayConsumed = null, onRunStateChange = null, onResponseReady = null, organizationId = null }) => {
  const resolvedOrganizationId = organizationId || localStorage.getItem('currentOrgId');
  const { isDarkMode } = useTheme();
  const { userSettings } = useUserSettingsContext();
  // Page context is READ here, never constructed here: pages publish semantic
  // state to the app-level provider, and the assistant consumes a normalized
  // snapshot. Absent on pages with no adapter — every path below tolerates null.
  const {
    snapshot: pageContextSnapshot,
    warning: pageContextWarning,
    clearSelection: clearPageSelection,
    applyServerFeedback: applyPageContextFeedback,
  } = useAssistantContext();
  const pageContextRef = useRef(null);
  pageContextRef.current = pageContextSnapshot;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  // Files staged in the composer for the NEXT message. Each entry:
  // { localId, name, size, status: 'uploading'|'ready'|'failed', attachment?, error? }
  // `attachment` is the server record (id, review summary) once uploaded —
  // only ready ids are sent with the message.
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const attachInputRef = useRef(null);
  // localId → upload promise (resolves to the server attachment or null) so a
  // message can be SENT while its files are still being reviewed: the send
  // waits on these, not on the chip state.
  const uploadPromisesRef = useRef(new Map());
  // Bumped by Stop so a send still waiting on file reviews is cancelled.
  const sendGenerationRef = useRef(0);
  // Every attachment id seen on this thread (sent or restored) so a workflow
  // launched from a button can read the same files as a typed question.
  const threadAttachmentIdsRef = useRef(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  // Mirrors for effects that must read the CURRENT thread/transcript without
  // re-running on every message (see the activeThreadId loader).
  const threadIdRef = useRef(null);
  const messagesRef = useRef([]);
  useEffect(() => { threadIdRef.current = threadId; }, [threadId]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [error, setError] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // Smart Router is the only assistant. `fast` selects its speed-optimized
  // OpenRouter pool; concrete models are never user-selectable.
  const [selectedModel, setSelectedModel] = useState(() =>
    localStorage.getItem('lodgeic-ai-fast-mode') === 'true' ? 'fast' : 'auto'
  );
  const [streamingStatus, setStreamingStatus] = useState(null);
  // Shape of the artifact currently being computed by a backend tool.
  // Set from status messages ("Loading financial data…" → "pnl"), cleared
  // when the real artifact arrives or tokens start flowing. Drives an
  // inline ChartPlaceholder skeleton during the otherwise-silent tool phase.
  // eslint-disable-next-line no-unused-vars
  const [pendingArtifactType, setPendingArtifactType] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [activeMenuSection, setActiveMenuSection] = useState(null); // 'location' | 'history' | 'prompts' | null
  const [selectedLocationIds, setSelectedLocationIds] = useState(new Set()); // multi-select property picker
  const [locationFilter, setLocationFilter] = useState('');

  // ----- v2 redesign state (used only when !compact) -----
  const [v2Popover, setV2Popover] = useState(null);  // 'properties' | 'prompts' | 'tools' | null
  const [v2Tray, setV2Tray] = useState(null);        // 'settings' | 'history' | 'automations' | null
  const [v2PropQuery, setV2PropQuery] = useState('');
  const [assistantSettings, setAssistantSettings] = useState({
    style: 'balanced', window: 'MTD',
    tables: true, charts: true, reasoning: false,
    pms: true, pnl: true, budget: true, forecast: true, labor: false, str: true,
  });

  useEffect(() => {
    localStorage.setItem('lodgeic-ai-fast-mode', selectedModel === 'fast' ? 'true' : 'false');
  }, [selectedModel]);

  useEffect(() => {
    if (!externalTray) return;
    setV2Tray(externalTray);
    if (onExternalTrayConsumed) onExternalTrayConsumed();
  }, [externalTray, onExternalTrayConsumed]);

  // Keep selectedLocationIds in sync with the parent's selectedHotelId prop.
  // This runs whenever the parent scope changes OR a new conversation is
  // started (activeThreadId === null). During a loaded historical session
  // (activeThreadId !== null) we skip this sync — loadConversation has
  // already restored selectedLocationIds from the persisted session, and
  // we don't want to overwrite it.
  //
  // The stricter sync is what keeps the send-side scope from going stale:
  // when the user clicks "New chat", the parent resets its scope to a
  // clean default for the current org; this effect mirrors that into
  // local selectedLocationIds so the next send uses the right ids.
  useEffect(() => {
    if (activeThreadId) return; // session-restore path owns the set
    if (selectedHotelId === 'all' && locations?.length) {
      setSelectedLocationIds(new Set(locations.map(l => l.id.toString())));
    } else if (selectedHotelId && selectedHotelId !== 'all') {
      setSelectedLocationIds(new Set([selectedHotelId.toString()]));
    } else {
      setSelectedLocationIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHotelId, locations?.length, activeThreadId]);
  /** Human name for a property id, for the scope-change marker. */
  const resolveHotelLabel = useCallback((id) => {
    if (!id || id === 'all') return 'all locations';
    const match = (locations || []).find(l => String(l.id) === String(id));
    return match?.name || selectedHotelName || null;
  }, [locations, selectedHotelName]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const plusMenuRef = useRef(null);
  const abortStreamRef = useRef(null);
  const streamingMessageIdRef = useRef(null);
  // Live thinking-trace for the in-flight message (v2 agent core emits
  // structured trace_step events; thinking deltas become italic thought
  // steps). Frozen onto the message as msg.trace/{traceDurationMs} on done.
  const [liveTrace, setLiveTrace] = useState(null); // { steps, startedAt, phase: 'thinking'|'writing' }
  const liveTraceRef = useRef(null);
  const updateLiveTrace = (updater) => {
    setLiveTrace(prev => {
      const next = updater(prev);
      liveTraceRef.current = next;
      return next;
    });
  };
  const tokenBufferRef = useRef('');
  const tokenDrainRef = useRef(null);
  const lastDrainTimeRef = useRef(0);

  // Scope context for the working card's sub-line while it gathers data.
  // Plain and stable ("10 properties"), never a tool name.
  const runScopeLabel = (() => {
    const n = selectedLocationIds?.size || 0;
    if (n > 1) return `${n} properties`;
    return null;
  })();

  // Active source-document viewer — non-null when user clicks a night-audit
  // reference card. `loading` gates the initial fetchImportPdf() round-trip.
  const [activeFileView, setActiveFileView] = useState(null);

  const openFileReference = async (ref) => {
    if (!ref || !ref.filePath) return;
    setActiveFileView({ loading: true, filePath: ref.filePath, filename: ref.filename, label: ref.label, url: null });
    try {
      const blobUrl = await fetchImportPdf(ref.filePath);
      setActiveFileView(prev => prev && prev.filePath === ref.filePath
        ? { ...prev, loading: false, url: blobUrl }
        : prev
      );
    } catch (err) {
      console.error('[AIChat] fetchImportPdf failed:', err);
      setActiveFileView(prev => prev && prev.filePath === ref.filePath
        ? { ...prev, loading: false, error: err.message || 'Failed to open file' }
        : prev
      );
    }
  };

  const closeFileReference = () => {
    setActiveFileView(prev => {
      if (prev?.url && prev.url.startsWith('blob:')) {
        try { URL.revokeObjectURL(prev.url); } catch (_) { /* ignore */ }
      }
      return null;
    });
  };

  const userScrolledUpRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
  };

  // Only auto-scroll when a NEW message is added (user sends or assistant appears),
  // NOT during streaming token updates. Let the user scroll freely while streaming.
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      // New message added — scroll to bottom and reset scroll flag
      userScrolledUpRef.current = false;
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Close the legacy plus menu when clicking outside.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(event.target)) {
        setShowPlusMenu(false);
        setActiveMenuSection(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * A scope change that arrived while a turn was still running. Held here and
   * flushed once the turn lands, so the marker never appears between a question
   * and its own answer — where it reads as if the answer had been re-scoped.
   */
  const pendingScopeNoticeRef = useRef(null);

  /**
   * Is a turn still running? Deliberately NOT `abortStreamRef`, which is only
   * ever assigned and never cleared on completion — reading it as "in flight"
   * would treat every conversation as permanently busy after its first turn.
   */
  const turnInFlightRef = useRef(false);

  const appendScopeNotice = useCallback((label) => {
    setMessages(prev => (prev.length === 0 ? prev : [...prev, {
      id: `scope-${Date.now()}`,
      // Not 'user' or 'assistant': conversation history is filtered to those
      // two, so this marker is never sent to the model or persisted as a turn.
      role: 'notice',
      content: `Now looking at ${label}. New questions will use this property; answers above kept the property they were asked from.`
    }]));
  }, []);

  const flushPendingScopeNotice = useCallback(() => {
    const label = pendingScopeNoticeRef.current;
    if (!label) return;
    pendingScopeNoticeRef.current = null;
    appendScopeNotice(label);
  }, [appendScopeNotice]);

  // Track previous hotel to only reset on actual user-initiated changes
  const prevHotelIdRef = useRef(selectedHotelId);
  useEffect(() => {
    const prev = prevHotelIdRef.current;
    prevHotelIdRef.current = selectedHotelId;

    // Only reset if the hotel actually changed from one value to another
    // (skip initial mount and null→value transitions from auto-detect)
    if (!prev || prev === selectedHotelId) return;

    // Skip when loading a historical session — loadConversation calls
    // onScopeRestore which flips selectedHotelId to the session's scope,
    // and we don't want that silent restore to race with (and wipe) the
    // messages loadConversation is about to set. User-initiated scope
    // changes go through handleHotelChange, which clears activeThreadId
    // first, so this guard only fires on the session-load path.
    if (activeThreadId) return;

    // In the ambient launcher the property changes as a SIDE EFFECT of
    // navigating, not because the user asked for a new conversation. A turn
    // belongs to the state it was asked in: the user asked about Oshkosh on
    // August 1, wandered off to another property while it was thinking, and the
    // answer came back re-labelled as Clearwater (2026-08-17). So navigation
    // must not touch a running turn at all — no abort, no clearing of the
    // streaming/loading state, and no notice wedged between the question and its
    // own answer.
    if (preserveHistoryOnScopeChange) {
      const label = resolveHotelLabel(selectedHotelId) || 'a different property';
      if (turnInFlightRef.current) {
        // A turn is in flight. Let it finish against the page it was sent from
        // and defer the marker until it has landed, so it reads as "from here
        // on" rather than as a correction to the answer above it.
        pendingScopeNoticeRef.current = label;
        return;
      }
      setError(null);
      appendScopeNotice(label);
      return;
    }

    // Explicit property change on the standalone page: the user chose a new
    // context, so the running turn is genuinely abandoned along with the
    // transcript below.
    if (tokenDrainRef.current) {
      cancelAnimationFrame(tokenDrainRef.current);
      tokenDrainRef.current = null;
    }
    tokenBufferRef.current = '';
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    if (onRunStateChange) onRunStateChange(false);
    setError(null);
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingStatus(null);
    setPendingArtifactType(null);

    setMessages([]);
    setThreadId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHotelId]);

  // Attachment bookkeeping follows the thread: a new thread starts with no
  // known attachments and no staged chips.
  useEffect(() => {
    if (!threadId) {
      threadAttachmentIdsRef.current = new Set();
      setPendingAttachments([]);
    }
  }, [threadId]);

  // Load conversation when activeThreadId changes. When the parent adopts the
  // id of the thread this component just created (onResponseReady after the
  // first answer), the transcript is already live in state — re-fetching it
  // would only race the server's save and, on any hiccup, paint "Failed to
  // load conversation" over a perfectly good answer.
  useEffect(() => {
    if (activeThreadId) {
      if (activeThreadId === threadIdRef.current && messagesRef.current.length > 0) return;
      loadConversation(activeThreadId);
    } else {
      setMessages([]);
      setThreadId(null);
    }
  }, [activeThreadId]);

  // Handle suggested prompt from sidebar highlights
  useEffect(() => {
    if (suggestedPrompt && selectedHotelId) {
      setInput(suggestedPrompt);
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 160) + 'px';
      }
      if (onSuggestedPromptUsed) {
        onSuggestedPromptUsed();
      }
    }
  }, [suggestedPrompt, selectedHotelId, onSuggestedPromptUsed]);

  const cleanUserMessage = (msg) => {
    if (msg.displayContent) {
      return msg.displayContent;
    }

    const content = typeof msg === 'string' ? msg : msg.content;

    if (content.startsWith('Portfolio Query -')) {
      const lines = content.split('\n');
      const questionLines = [];

      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (!line) continue;

        if (line.startsWith('## ') ||
            line.startsWith('**') ||
            line.startsWith('| ') ||
            line.startsWith('- ') ||
            line.startsWith('\u26A0\uFE0F') ||
            line.startsWith('\uD83D\uDCCA') ||
            line.startsWith('\u2705') ||
            line.includes('COMPREHENSIVE') ||
            line.includes('IMPORTANT CONTEXT') ||
            line.includes('Location IDs:') ||
            line.includes('Current Date/Time:')) {
          break;
        }

        questionLines.unshift(line);
      }

      if (questionLines.length > 0) {
        return questionLines.join('\n').trim();
      }
    }

    if (content.startsWith('Hotel ID:')) {
      const lines = content.split('\n');
      const questionLines = [];

      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (!line) continue;

        if (line.startsWith('## ') ||
            line.startsWith('**') ||
            line.startsWith('| ') ||
            line.startsWith('- ') ||
            line.startsWith('\u26A0\uFE0F') ||
            line.startsWith('\uD83D\uDCCA') ||
            line.includes('COMPREHENSIVE') ||
            line.includes('IMPORTANT CONTEXT') ||
            line.includes('Hotel ID:') ||
            line.includes('Current Date/Time:')) {
          break;
        }

        questionLines.unshift(line);
      }

      if (questionLines.length > 0) {
        return questionLines.join('\n').trim();
      }
    }

    return content;
  };

  const loadConversation = async (threadIdToLoad) => {
    try {
      setIsLoadingHistory(true);
      setError(null);

      const response = await getAIChatSession(threadIdToLoad);

      if (response.success && response.session) {
        const session = response.session;

        // Cross-org / deleted-property guard. If the session's hotelId
        // isn't 'all' and isn't in the user's current-org locations,
        // don't load it — the resulting scope restore would pin the
        // parent to a property the user can't query, leading to stale-
        // hotelId sends (2026-04-22 cross-org leak).
        if (session.hotelId && session.hotelId !== 'all' && Array.isArray(locations) && locations.length > 0) {
          const accessible = locations.some(l => String(l.id) === String(session.hotelId));
          if (!accessible) {
            console.warn(`Session ${threadIdToLoad} references hotelId=${session.hotelId} which isn't in the current org; not loading.`);
            setError("This conversation references a property that isn't available in your current organization.");
            setMessages([]);
            setThreadId(null);
            return;
          }
        }

        // Model selection was retired. Fast is a global user toggle, so loading
        // an old thread never changes it.

        // Restore the scope filter the user had applied when they last
        // sent a message in this session. Legacy sessions without a
        // persisted `selectedLocationIds` derive the list from hotelId.
        const sessionScope = Array.isArray(session.selectedLocationIds)
          ? session.selectedLocationIds
          : (session.hotelId === 'all'
              ? (locations || []).map(l => String(l.id))
              : session.hotelId ? [String(session.hotelId)] : []);
        if (sessionScope.length > 0) {
          setSelectedLocationIds(new Set(sessionScope));
        }
        // Also flow the scope back up to the parent so the top-right scope
        // label matches the session. `onScopeRestore` is a read-only
        // cousin of `onHotelChange` — it updates the label without
        // clearing activeThreadId (onHotelChange clears it as a side
        // effect to start a fresh conversation on manual scope switches).
        if (typeof onScopeRestore === 'function' && session.hotelId) {
          onScopeRestore(String(session.hotelId));
        }

        if (session.conversationHistory && session.conversationHistory.length > 0) {
          // Fallback timestamp: session.lastUpdated or createdAt if individual
          // messages lack timestamps (older saved sessions predate the fix).
          const fallbackTs = session.lastUpdated || session.createdAt || null;
          let loadedMessages = session.conversationHistory
            .filter(msg => msg.role === 'user' || msg.role === 'assistant')
            .map((msg, idx) => ({
              id: Date.now() + idx,
              role: msg.role,
              content: msg.role === 'user' ? cleanUserMessage(msg) : msg.content,
              timestamp: msg.timestamp ? new Date(msg.timestamp)
                : fallbackTs ? new Date(fallbackTs)
                : null,
              processingTime: msg.processingTime || null,
              analysisState: msg.analysisState || null,
              dataBasis: msg.dataBasis || null,
              sources: msg.sources || [],
              fileReferences: Array.isArray(msg.fileReferences) ? msg.fileReferences : [],
              // Files the user attached to this message (chips on the bubble).
              attachments: msg.role === 'user' && Array.isArray(msg.attachments) ? msg.attachments : [],
              // v2 agent core persists data_table artifacts on the assistant
              // turn so session reload re-renders the interactive tables.
              dataTables: Array.isArray(msg.artifacts)
                ? msg.artifacts.filter(a => a && a.kind === 'data_table')
                : [],
              pnlReports: Array.isArray(msg.artifacts)
                ? msg.artifacts.filter(a => a && a.kind === 'pnl_report').map(a => a.data || a.payload || a.pnl || a)
                : [],
              // v2 agent core persists the thinking trace for the collapsed
              // "Thought process" card on reload.
              ...(Array.isArray(msg.trace) && msg.trace.length > 0
                ? { trace: msg.trace, traceDurationMs: msg.traceDurationMs || msg.processingTime || 0 }
                : {}),
              workflowRunId: msg.workflowRunId || msg.workflowContext?.workflowRunId || null,
              // Workflow decisions are organization-scoped API writes. Keep
              // that scope on restored messages so a reload cannot render a
              // live card whose buttons submit `currentOrgId: undefined`.
              orgId: msg.orgId || session.currentOrgId || session.orgId || resolvedOrganizationId,
              threadId: threadIdToLoad
            }));

          // Workflow cards are durable application state, not serialized chat
          // markup. Rehydrate each referenced run from the authorized endpoint
          // so a reloaded/history conversation restores the live approval
          // controls and current proposal states.
          const runIds = [...new Set(loadedMessages.map(message => message.workflowRunId).filter(Boolean))];
          if (runIds.length > 0 && resolvedOrganizationId) {
            const hydrated = new Map();
            await Promise.all(runIds.map(async runId => {
              try {
                const data = await getWorkflowRun(runId, resolvedOrganizationId);
                if (data?.run) hydrated.set(runId, adaptStoredWorkflowRun(data.run));
              } catch (err) {
                console.warn(`Could not restore workflow run ${runId}:`, err?.message || err);
              }
            }));
            const latestRunMessage = new Map();
            loadedMessages.forEach((message, index) => {
              if (message.workflowRunId && hydrated.has(message.workflowRunId)) {
                latestRunMessage.set(message.workflowRunId, index);
              }
            });
            loadedMessages = loadedMessages
              .map((message, index) => message.workflowRunId && hydrated.has(message.workflowRunId)
                && latestRunMessage.get(message.workflowRunId) === index
                ? { ...message, workflowRun: hydrated.get(message.workflowRunId) }
                : message)
              // A tool-driven card turn often has no prose. When a later turn
              // re-emits the same durable run, keep only the newest live card
              // instead of stacking stale copies with actionable buttons.
              .filter((message, index) => message.content
                || message.role !== 'assistant'
                || !message.workflowRunId
                || latestRunMessage.get(message.workflowRunId) === index);
          }

          if (loadedMessages.length > 0) {
            threadAttachmentIdsRef.current = new Set(
              loadedMessages.flatMap(m => (Array.isArray(m.attachments) ? m.attachments : []).map(a => a.id)).filter(Boolean)
            );
            setMessages(loadedMessages);
            setThreadId(threadIdToLoad);
          } else {
            console.warn('Session found but no user/assistant messages');
            setError('No conversation history found');
          }
        } else {
          console.warn('Session found but no conversation history');
          setError('No conversation history found');
        }
      } else {
        console.warn('Failed to load session:', response.error);
        setError(`Failed to load conversation${response?.error ? ` (${response.error})` : ''}`);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.error || (status ? `HTTP ${status}` : error?.message);
      setError(`Failed to load conversation${detail ? ` (${detail})` : ''}`);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const sendMessageStreaming = (questionText, displayText, promptKey = null, hotelIdOverride = null, attachmentIds = []) => {
    if (onRunStateChange) onRunStateChange(true);
    turnInFlightRef.current = true;
    // Read once: the user may navigate mid-request, and this turn belongs to
    // the page it was sent from.
    const turnPageContext = pageContextRef.current;
    const currentOrgId = resolvedOrganizationId;
    const assistantMsgId = Date.now() + 1;
    streamingMessageIdRef.current = assistantMsgId;
    // Open the Thinking card immediately — every loading state (classify,
    // prefetch, tools, reasoning) lives inside it; no standalone orbs.
    updateLiveTrace(() => ({ steps: [], startedAt: Date.now(), phase: 'thinking' }));

    // Resolve the scope to send. `selectedLocationIds` is the authoritative
    // local state (multi-select popover), so derive hotelId from it rather
    // than from the parent's `selectedHotelId` prop — which can go stale
    // when a cross-org session-resume leaves the parent pinned to a hotelId
    // that isn't in the user's current org (2026-04-22 "restricted to
    // location 20" bug).
    //
    // Additional guard: if the resolved id is a single property, confirm
    // it's in the current `locations` list. If not, fall back to 'all' —
    // sending an unknown hotelId would either 403 or (worse) succeed with
    // a cross-org scope pin.
    const accessibleIds = new Set((locations || []).map(l => String(l.id)));
    let resolvedHotelId;
    if (hotelIdOverride) {
      resolvedHotelId = hotelIdOverride;
    } else if (selectedLocationIds.size === 0) {
      // No local selection yet — trust the parent prop if we recognize it.
      resolvedHotelId = selectedHotelId && (selectedHotelId === 'all' || accessibleIds.has(String(selectedHotelId)))
        ? selectedHotelId
        : (locations && locations.length > 1 ? 'all' : (locations && locations.length === 1 ? String(locations[0].id) : null));
    } else if (selectedLocationIds.size === 1) {
      const onlyId = [...selectedLocationIds][0];
      resolvedHotelId = accessibleIds.has(String(onlyId)) ? String(onlyId) : 'all';
    } else if (locations && selectedLocationIds.size >= locations.length) {
      // User has every property in the current org selected → treat as 'all'.
      resolvedHotelId = 'all';
    } else {
      // Partial multi-select (e.g. 3 of 22). Backend portfolio path doesn't
      // yet honor arbitrary subsets at the locationIds level, so fall back
      // to 'all' and rely on the SQL-scope enforcement to clip it.
      resolvedHotelId = 'all';
    }

    // A registered page adapter is the default scope for a turn sent from
    // that page. This is what makes “fix this journal” use the journal's
    // property even if the floating chat previously followed the user from a
    // different hotel. An explicit property resolved from the typed question
    // (hotelIdOverride) still wins, and explicit portfolio language stays
    // portfolio-scoped.
    const pageScope = resolveAssistantTurnScope({
      question: questionText,
      hotelIdOverride,
      fallbackHotelId: resolvedHotelId,
      pageContext: turnPageContext?.envelope || null,
      accessibleIds,
    });
    resolvedHotelId = pageScope.hotelId;
    const pageScopedHotelId = pageScope.pageScopedHotelId;

    // Derive the persisted scope array. When the caller passes a specific
    // hotelIdOverride (e.g. auto-scope narrowing on "river lodge…"), use it
    // as the authoritative source so the outgoing request AND the saved
    // session reflect the narrowed scope even before React commits the
    // setSelectedLocationIds update — the user's "scope must narrow BEFORE
    // the question is processed" requirement.
    let scopedLocationIdsToSend;
    if (pageScopedHotelId) {
      scopedLocationIdsToSend = [pageScopedHotelId];
    } else if (hotelIdOverride && hotelIdOverride !== 'all' && accessibleIds.has(String(hotelIdOverride))) {
      scopedLocationIdsToSend = [String(hotelIdOverride)];
    } else if (hotelIdOverride === 'all' || (resolvedHotelId === 'all' && PORTFOLIO_RE.test(questionText))) {
      scopedLocationIdsToSend = (locations || []).map(l => String(l.id));
    } else {
      // Normalize the persisted scope array: drop any ids not accessible in
      // the current org so a stale multi-select doesn't carry across orgs.
      scopedLocationIdsToSend = [...selectedLocationIds]
        .map(String)
        .filter(id => accessibleIds.has(id));
    }

    // Add empty assistant message that will fill in as tokens arrive
    const assistantMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      sources: [],
      timestamp: new Date(),
      processingTime: null,
      webSearchesExecuted: 0,
      webSearchSources: [],
      rating: null,
      isStreaming: true
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsStreaming(true);
    setStreamingStatus('Connecting...');

    // Smooth token drain: buffer incoming tokens, render in steady chunks via rAF
    const startTokenDrain = () => {
      if (tokenDrainRef.current) return; // Already running
      const drain = (timestamp) => {
        if (tokenBufferRef.current.length > 0) {
          // Drain more characters when buffer is large to prevent falling behind
          const bufLen = tokenBufferRef.current.length;
          const charsPerFrame = bufLen > 200 ? 20 : bufLen > 50 ? 8 : 3;
          const chunk = tokenBufferRef.current.slice(0, charsPerFrame);
          tokenBufferRef.current = tokenBufferRef.current.slice(charsPerFrame);
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMsgId
              ? { ...msg, content: msg.content + chunk }
              : msg
          ));
        }
        tokenDrainRef.current = requestAnimationFrame(drain);
      };
      tokenDrainRef.current = requestAnimationFrame(drain);
    };

    const flushTokenBuffer = () => {
      // Stop the drain loop and flush remaining buffer immediately
      if (tokenDrainRef.current) {
        cancelAnimationFrame(tokenDrainRef.current);
        tokenDrainRef.current = null;
      }
      if (tokenBufferRef.current.length > 0) {
        const remaining = tokenBufferRef.current;
        tokenBufferRef.current = '';
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMsgId
            ? { ...msg, content: msg.content + remaining }
            : msg
        ));
      }
    };

    const abort = queryHotelDataStream(
      {
        hotelId: resolvedHotelId,
        question: questionText,
        threadId,
        currentOrgId,
        model: selectedModel,
        promptKey,
        // Persisted per-session so reloading the thread restores the exact
        // multi-select scope the user had active (see saveConversation in
        // azfApi/fastify/routes/ai/queryHotelData.js). Filtered to the
        // current-org accessible set so cross-org ids can't leak.
        selectedLocationIds: scopedLocationIdsToSend,
        // The page snapshot AT SEND TIME. Captured per turn, not per thread:
        // a conversation may legitimately move between pages, and each turn
        // keeps the context it was actually asked in.
        pageContext: turnPageContext?.envelope || null,
        // Files attached to THIS message; the server also re-attaches every
        // earlier file on the thread so follow-ups keep working.
        ...(Array.isArray(attachmentIds) && attachmentIds.length ? { attachmentIds } : {})
      },
      {
        onToken: (token) => {
          setStreamingStatus(null);
          setPendingArtifactType(null); // real tokens are flowing; drop placeholder
          // Answer is streaming: the live trace flips to its finished look
          // ("Writing answer…" phase) and auto-collapses on render.
          updateLiveTrace(prev => (prev && prev.phase !== 'writing' ? { ...prev, phase: 'writing' } : prev));
          tokenBufferRef.current += token;
          startTokenDrain();
        },
        // Raw model reasoning is no longer rendered anywhere, and the server
        // no longer sends it (see onThinking in routes/ai/queryHotelData.js).
        // It ran to thousands of tokens on workflow turns, changed faster than
        // anyone could read, and could restate data the asking user is not
        // entitled to. The working card reports progress from `status` and
        // `trace_step` instead. The handler stays as a no-op so an older
        // server still streaming the event cannot put reasoning on screen.
        onThinking: () => {},
        onWorkflowRun: (workflowRun) => {
          // A routed workflow answers with an artifact, not prose. Attach it to
          // the streaming message so it renders through the same card the
          // native action uses.
          setPendingArtifactType(null);
          applyPageContextFeedback(workflowRun?.pageContext || null, turnPageContext?.hash || null);
          const incomingRunId = workflowRun?.run?.workflowRunId;
          setMessages(prev => prev
            .filter(msg => !(incomingRunId
              && msg.id !== assistantMsgId
              && msg.workflowRun?.run?.workflowRunId === incomingRunId
              && !String(msg.content || '').trim()))
            .map(msg => {
              if (msg.id === assistantMsgId) {
                return { ...msg, workflowRun, workflowRunId: incomingRunId, orgId: currentOrgId, contextSnapshotLabel: workflowRun?.pageContext?.label || msg.contextSnapshotLabel };
              }
              if (incomingRunId && msg.workflowRun?.run?.workflowRunId === incomingRunId) {
                const { workflowRun: _staleCard, ...withoutStaleCard } = msg;
                return withoutStaleCard;
              }
              return msg;
            }));
        },
        onTraceStep: (step) => {
          // Structured tool steps from the v2 agent core. Same-id steps merge
          // (running → done/error).
          if (!step) return;
          updateLiveTrace(prev => {
            const base = prev || { steps: [], startedAt: Date.now(), phase: 'thinking' };
            const steps = [...base.steps];
            const idx = step.id != null ? steps.findIndex(s => s.id === step.id) : -1;
            if (idx >= 0) steps[idx] = { ...steps[idx], ...step };
            else steps.push(step);
            return { ...base, steps, phase: 'thinking' };
          });
        },
        onPnlReport: (pnlData) => {
          setPendingArtifactType(null); // artifact arrived
          if (!pnlData) return;
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMsgId
              ? {
                  ...msg,
                  pnlReports: Array.isArray(msg.pnlReports) && msg.pnlReports.length > 0
                    ? msg.pnlReports
                    : [pnlData],
                }
              : msg
          ));
        },
        onForecastProposal: (proposal) => {
          setPendingArtifactType(null); // artifact arrived
          // Inject forecast_changes block into the message content so renderMessageContent parses it
          const forecastBlock = '\n```forecast_changes\n' + JSON.stringify(proposal, null, 2) + '\n```\n';
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMsgId
              ? { ...msg, content: msg.content + forecastBlock }
              : msg
          ));
        },
        onFileReferences: (files) => {
          // Night-audit source-document cards from analyze_source_documents.
          // Attach to the streaming message so the render block below picks
          // them up. Merge + dedupe by importId so repeat emissions within a
          // single turn don't duplicate.
          if (!Array.isArray(files) || files.length === 0) return;
          setMessages(prev => prev.map(msg => {
            if (msg.id !== assistantMsgId) return msg;
            const existing = Array.isArray(msg.fileReferences) ? msg.fileReferences : [];
            const seen = new Set(existing.map(f => f.importId));
            const merged = [...existing, ...files.filter(f => !seen.has(f.importId))];
            return { ...msg, fileReferences: merged };
          }));
        },
        onDataTable: (table) => {
          // Generic query-result table from the v2 agent core. Attach to the
          // message object (fileReferences pattern) — NOT injected into
          // content, because onDone replaces content with the sanitized
          // final answer and would drop injected blocks. Dedupe by
          // toolName+title so loop retries don't duplicate tables.
          if (!table || !Array.isArray(table.rows) || table.rows.length === 0) return;
          setPendingArtifactType(null);
          setMessages(prev => prev.map(msg => {
            if (msg.id !== assistantMsgId) return msg;
            const existing = Array.isArray(msg.dataTables) ? msg.dataTables : [];
            const key = `${table.toolName || ''}|${table.title || ''}`;
            if (existing.some(t => `${t.toolName || ''}|${t.title || ''}` === key)) return msg;
            return { ...msg, dataTables: [...existing, table] };
          }));
        },
        onStatus: (message) => {
          setStreamingStatus(message);
          const t = artifactTypeFromStatus(message);
          if (t) setPendingArtifactType(t);
        },
        onDone: (data) => {
          flushTokenBuffer();
          turnInFlightRef.current = false;
          setIsStreaming(false);
          setIsLoading(false);
          // A property switch that arrived mid-answer lands AFTER this turn.
          flushPendingScopeNotice();
          setStreamingStatus(null);
          setPendingArtifactType(null);
          streamingMessageIdRef.current = null;

          // Reconcile the page context with what the server actually resolved
          // for THIS turn — a selection that no longer exists is dropped and
          // reported, never silently replaced by a different row.
          applyPageContextFeedback(data?.metadata?.pageContext || null, turnPageContext?.hash || null);

          // Freeze the live trace onto the message so the collapsed
          // "Thought process · N tools · X.Xs" card persists for this turn.
          const finishedTrace = liveTraceRef.current;
          const frozenSteps = finishedTrace?.steps?.length
            ? finishedTrace.steps.map(s => (s.state === 'running' || s.state === 'queued') ? { ...s, state: 'done' } : s)
            : null;
          const frozenDuration = finishedTrace ? Date.now() - finishedTrace.startedAt : null;
          updateLiveTrace(() => null);

          // Finalize the message with metadata. Prefer the sanitized final
          // answer from the done event — the backend runs post-stream leak
          // stripping that catches leaks the real-time filter missed. Falling
          // back to streamed content only when no final answer was returned.
          setMessages(prev => prev.map(msg => {
            if (msg.id !== assistantMsgId) return msg;
            // Prefer in-flight fileReferences collected via SSE (fresh);
            // fall back to metadata (sync path / eval runner).
            const fileRefs = Array.isArray(msg.fileReferences) && msg.fileReferences.length > 0
              ? msg.fileReferences
              : (data.metadata?.fileReferences || []);
            const pnlReports = Array.isArray(msg.pnlReports) && msg.pnlReports.length > 0
              ? msg.pnlReports
              : (data.metadata?.pnlReport ? [data.metadata.pnlReport] : []);
            return {
              ...msg,
              // A workflow run is the answer. Keeping the done-event narrative
              // above its card made the same conclusion render twice.
              content: msg.workflowRun ? '' : (data.answer || msg.content),
              isStreaming: false,
              // Server-minted id for this assistant turn. Carried purely so a
              // rating can be attributed to the runtime/model that produced
              // the answer (see aiChatFeedback).
              turnId: data.metadata?.turnId || null,
              analysisState: data.metadata?.analysisState || null,
              dataBasis: data.metadata?.dataBasis || null,
              processingTime: data.metadata?.processingTime,
              threadId: data.threadId,
              webSearchesExecuted: data.metadata?.webSearchesExecuted || 0,
              webSearchSources: data.metadata?.webSearchSources || [],
              fileReferences: fileRefs,
              pnlReports,
              ...(frozenSteps ? { trace: frozenSteps, traceDurationMs: frozenDuration } : {})
            };
          }));

          const newThreadId = data.threadId;
          if (newThreadId) setThreadId(newThreadId);
          if (onSessionUpdated && newThreadId) {
            onSessionUpdated(newThreadId, displayText);
          }
          if (onRunStateChange) onRunStateChange(false);
          if (onResponseReady) onResponseReady({ kind: 'response', threadId: newThreadId || threadId });
        },
        onError: (err) => {
          console.error('Streaming error, falling back to non-streaming:', err);
          // Clean up token buffer
          if (tokenDrainRef.current) {
            cancelAnimationFrame(tokenDrainRef.current);
            tokenDrainRef.current = null;
          }
          tokenBufferRef.current = '';
          // Remove the empty streaming message
          setMessages(prev => prev.filter(msg => msg.id !== assistantMsgId));
          setIsStreaming(false);
          setStreamingStatus(null);
          setPendingArtifactType(null);
          updateLiveTrace(() => null);
          streamingMessageIdRef.current = null;

          // Fall back to non-streaming request
          queryHotelData(resolvedHotelId, questionText, threadId, currentOrgId, selectedModel, promptKey, scopedLocationIdsToSend, turnPageContext?.envelope || null, attachmentIds)
            .then(response => {
              if (response.success) {
                setMessages(prev => [...prev, {
                  id: Date.now() + 1,
                  role: 'assistant',
                  content: response.workflowRun ? '' : response.answer,
                  sources: response.sources || [],
                  turnId: response.metadata?.turnId || null,
                  threadId: response.metadata.threadId,
                  timestamp: new Date(),
                  processingTime: response.metadata.processingTime,
                  webSearchesExecuted: response.metadata.webSearchesExecuted || 0,
                  webSearchSources: response.metadata.webSearchSources || [],
                  fileReferences: response.metadata.fileReferences || [],
                  pnlReports: response.metadata.pnlReport ? [response.metadata.pnlReport] : [],
                  workflowRun: response.workflowRun || null,
                  rating: null
                }]);
                const tid = response.threadId || response.metadata?.threadId;
                if (tid) setThreadId(tid);
                if (onSessionUpdated && tid) onSessionUpdated(tid, displayText);
                if (onResponseReady) onResponseReady({ kind: 'response', threadId: tid || threadId });
              } else {
                throw new Error(response.error || 'Failed to get response');
              }
            })
            .catch(fallbackErr => {
              setError(fallbackErr.message || 'Failed to send message.');
              setMessages(prev => [...prev, { id: Date.now() + 1, role: 'error', content: fallbackErr.message || 'Failed to get response.', timestamp: new Date() }]);
            })
            .finally(() => {
              turnInFlightRef.current = false;
              setIsLoading(false);
              if (onRunStateChange) onRunStateChange(false);
              flushPendingScopeNotice();
            });
        }
      }
    );

    abortStreamRef.current = abort;
  };

  // Auto-scope: when a user's plain-text question names ONE property (and
  // only one), narrow the scope chip to that property BEFORE the request
  // fires. If they name a different property in a later turn, swap again.
  // 0 or >1 matches → leave current scope alone (handles comparisons and
  // pronoun-only follow-ups).
  //
  // Returns { id, name } when a switch was applied, else null. The caller
  // uses the returned id as hotelIdOverride so the send uses the narrowed
  // scope even before React commits the setSelectedLocationIds update.
  const applyScopeNarrowingIfMentioned = (question) => {
    if (!locations || locations.length <= 1) return null;
    const mentioned = detectMentionedProperties(question, locations);
    if (mentioned.length !== 1) return null;

    const match = mentioned[0];
    const currentIsSame = selectedLocationIds.size === 1
      && [...selectedLocationIds][0] === match.id;
    if (currentIsSame) return null;

    setSelectedLocationIds(new Set([match.id]));
    // Pre-update the hotel-reset guard ref BEFORE the parent sync. The
    // [selectedHotelId] useEffect wipes messages/aborts streams on any
    // hotel change to handle user-initiated picker switches — but our
    // mid-turn auto-narrow is coordinated with a send, so we must
    // short-circuit that guard. By writing match.id first, when the effect
    // runs post-commit it reads prev===selectedHotelId and returns early.
    prevHotelIdRef.current = match.id;
    // Keep the parent's selectedHotelId/Name in sync so anything rendered
    // from the parent (and the scope persistence path) matches. Uses the
    // silent-restore callback — does NOT reset activeThreadId, so the
    // current conversation continues under the narrowed scope.
    if (typeof onScopeRestore === 'function') {
      onScopeRestore(match.id);
    }
    return match;
  };

  // Resolve hotel context from question text + available locations.
  // Primary source of truth for the request's hotelId. Relies on
  // applyScopeNarrowingIfMentioned having already been called upstream if
  // the caller wants the chip to follow the switch.
  const resolveHotelFromQuestion = (question) => {
    if (locations?.length > 1) {
      const matches = detectMentionedProperties(question, locations);
      if (matches.length === 1) return matches[0].id.toString();
    }

    if (selectedLocationIds.size === 1) {
      return [...selectedLocationIds][0];
    }
    if (selectedLocationIds.size > 1) {
      return 'all';
    }

    if (selectedHotelId) return selectedHotelId;

    if (locations?.length === 1) {
      return locations[0].id.toString();
    }

    if (locations?.length > 1) return 'all';

    return null;
  };

  // Derive the location badge text
  const getLocationBadgeText = () => {
    if (selectedLocationIds.size === 1) {
      const id = [...selectedLocationIds][0];
      const loc = locations?.find(l => l.id.toString() === id);
      return loc?.name || id;
    }
    if (selectedLocationIds.size > 1) {
      return `${selectedLocationIds.size} Properties`;
    }
    if (selectedHotelName) return selectedHotelName;
    if (locations?.length === 1) return locations[0].name;
    if (locations?.length > 1) return 'All Locations';
    return null;
  };

  // ── Attachments ────────────────────────────────────────────────────────
  const readyAttachments = pendingAttachments.filter(a => a.status === 'ready' && a.attachment?.id);
  const hasUploadingAttachments = pendingAttachments.some(a => a.status === 'uploading');
  // Sending is allowed while files are still being reviewed — the send waits
  // for them (see handleSendMessage). Only failed-only staging blocks a send
  // with no text.
  const canSend = input.trim().length > 0 || readyAttachments.length > 0 || hasUploadingAttachments;

  const addAttachmentFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const orgId = parseInt(resolvedOrganizationId || '0', 10) || null;
    const hotelForUpload = selectedHotelId && selectedHotelId !== 'all' ? selectedHotelId : null;
    files.forEach((file) => {
      const localId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setPendingAttachments(prev => [...prev, { localId, name: file.name, size: file.size, status: 'uploading' }]);
      const promise = uploadChatAttachment(file, { hotelId: hotelForUpload, threadId, currentOrgId: orgId })
        .then((res) => {
          const attachment = res?.attachment;
          if (!res?.success || !attachment) throw new Error(res?.error || 'Upload failed');
          setPendingAttachments(prev => prev.map(a => a.localId === localId
            ? { ...a, status: attachment.status === 'failed' ? 'failed' : 'ready', attachment, error: attachment.error || null }
            : a));
          // A file the server could not read is still shown on the message,
          // but its id is not sent (the model would only see "failed").
          return attachment.status === 'failed' ? { failed: attachment } : { attachment };
        })
        .catch((err) => {
          const message = err?.response?.data?.error || err?.message || 'Upload failed';
          setPendingAttachments(prev => prev.map(a => a.localId === localId ? { ...a, status: 'failed', error: message } : a));
          return { failed: { fileName: file.name, error: message } };
        });
      uploadPromisesRef.current.set(localId, promise);
    });
  }, [resolvedOrganizationId, selectedHotelId, threadId]);

  const removePendingAttachment = (localId) => {
    uploadPromisesRef.current.delete(localId);
    setPendingAttachments(prev => prev.filter(a => a.localId !== localId));
  };

  const handleComposerDrop = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    setIsDragOver(false);
    addAttachmentFiles(e.dataTransfer.files);
  };
  const handleComposerDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };
  const handleComposerDragLeave = () => setIsDragOver(false);
  const handleComposerPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    addAttachmentFiles(files);
  };

  const renderAttachmentButton = (className) => (
    <>
      <input
        ref={attachInputRef}
        type="file"
        multiple
        accept={CHAT_ATTACHMENT_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => { addAttachmentFiles(e.target.files); e.target.value = ''; }}
      />
      <button
        type="button"
        className={className}
        onClick={() => attachInputRef.current?.click()}
        title="Attach a file (xlsx, csv, pdf, docx, image) — notes, checklists, budgets"
        aria-label="Attach file"
      >
        <IconPaperclip size={14} />
      </button>
    </>
  );

  const renderPendingAttachments = () => pendingAttachments.length > 0 && (
    <div className="ai-attachment-row" role="list" aria-label="Attached files">
      {pendingAttachments.map((a) => (
        <div key={a.localId} className={`ai-attachment-chip is-${a.status}`} role="listitem" title={a.status === 'failed' ? (a.error || 'Upload failed') : (a.attachment?.review?.summary || a.name)}>
          <span className="ai-attachment-icon" aria-hidden="true">
            {a.status === 'uploading' ? <span className="ai-attachment-spinner" /> : '📎'}
          </span>
          <span className="ai-attachment-name">{a.name}</span>
          <span className="ai-attachment-meta">
            {a.status === 'uploading' ? 'reviewing…' : a.status === 'failed' ? 'failed' : (a.attachment?.review?.documentType || 'ready')}
          </span>
          <button type="button" className="ai-attachment-remove" onClick={() => removePendingAttachment(a.localId)} aria-label={`Remove ${a.name}`}>
            <IconX size={11} />
          </button>
        </div>
      ))}
    </div>
  );

  /**
   * Stop the in-flight generation. Aborts the SSE fetch (the api helper
   * swallows the AbortError so the non-streaming fallback does NOT fire),
   * keeps whatever answer text already arrived, and returns the composer to
   * an idle state. The server finishes its turn on its own; the partial
   * answer is what the user keeps client-side.
   */
  const stopGeneration = () => {
    sendGenerationRef.current += 1;
    if (abortStreamRef.current) {
      try { abortStreamRef.current(); } catch { /* already closed */ }
      abortStreamRef.current = null;
    }
    if (tokenDrainRef.current) {
      cancelAnimationFrame(tokenDrainRef.current);
      tokenDrainRef.current = null;
    }
    const pendingText = tokenBufferRef.current || '';
    tokenBufferRef.current = '';
    const stoppedId = streamingMessageIdRef.current;
    if (stoppedId != null) {
      setMessages(prev => prev.flatMap(msg => {
        if (msg.id !== stoppedId) return [msg];
        const content = `${msg.content || ''}${pendingText}`;
        // Nothing arrived yet → drop the empty bubble rather than show a blank.
        if (!content.trim() && !msg.workflowRun) return [];
        return [{ ...msg, content, stopped: true }];
      }));
    }
    streamingMessageIdRef.current = null;
    turnInFlightRef.current = false;
    setIsStreaming(false);
    setIsLoading(false);
    setStreamingStatus(null);
    setPendingArtifactType(null);
    updateLiveTrace(() => null);
    if (onRunStateChange) onRunStateChange(false);
  };

  const isGenerating = isLoading || isStreaming;

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (isGenerating) return; // typing is allowed mid-turn; sending waits for Stop/finish
    if (!canSend) return;

    // Narrow scope chip BEFORE resolving the send id, so both the UI and
    // the request agree on the single-property scope for this turn and
    // every follow-up until the user names a different property.
    const narrowed = applyScopeNarrowingIfMentioned(input);
    const resolvedHotelId = narrowed ? narrowed.id : resolveHotelFromQuestion(input);
    if (!resolvedHotelId) {
      setError('No properties available. Please check your permissions.');
      return;
    }

    // Snapshot the staged files: their uploads may still be running. The
    // message goes up immediately (chips show "reviewing…"), and the request
    // fires once every review has settled — the user never waits on the model.
    const staged = pendingAttachments.filter(a => a.status !== 'failed');
    const stagedPromises = staged.map(a => uploadPromisesRef.current.get(a.localId) || Promise.resolve(a.attachment ? { attachment: a.attachment } : { failed: { fileName: a.name } }));
    const stagedCount = staged.length;
    // An attachment with no text still needs a question for the model.
    const userDisplayText = input.trim()
      ? input
      : `Please review the attached file${stagedCount === 1 ? '' : 's'} and summarize what ${stagedCount === 1 ? 'it contains' : 'they contain'}.`;
    const apiMessage = contextPrefix ? `${contextPrefix}${userDisplayText}` : userDisplayText;

    const userMessageId = Date.now();
    const userMessage = {
      id: userMessageId,
      role: 'user',
      content: userDisplayText,
      displayContent: userDisplayText,
      timestamp: new Date(),
      ...(stagedCount ? { attachments: staged.map(a => ({ id: a.attachment?.id || a.localId, fileName: a.name, contentType: a.attachment?.contentType || null, size: a.size, status: a.status })) } : {})
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setPendingAttachments([]);
    staged.forEach(a => uploadPromisesRef.current.delete(a.localId));
    setIsLoading(true);
    setError(null);
    if (stagedCount && staged.some(a => a.status === 'uploading')) {
      setStreamingStatus(`Reviewing ${stagedCount} attached file${stagedCount === 1 ? '' : 's'}…`);
    }

    // Briefing handoff contexts are one-shot — clear after first send so the
    // preamble doesn't ride on every subsequent turn.
    if (contextPrefix && onContextPrefixConsumed) onContextPrefixConsumed();

    const sendGeneration = ++sendGenerationRef.current;
    const settled = stagedCount ? await Promise.all(stagedPromises) : [];
    if (sendGenerationRef.current !== sendGeneration) return; // stopped while files were being reviewed
    const attachmentsToSend = settled.map(r => r && r.attachment).filter(Boolean);
    const attachmentIds = attachmentsToSend.map(a => a.id);
    attachmentIds.forEach(id => threadAttachmentIdsRef.current.add(id));
    if (stagedCount) {
      // Reconcile the chips on the sent bubble with the server outcome.
      const byName = new Map(settled.map((r, i) => [staged[i].localId, r]));
      setMessages(prev => prev.map(m => m.id !== userMessageId ? m : {
        ...m,
        attachments: staged.map(a => {
          const r = byName.get(a.localId);
          if (r?.attachment) return { id: r.attachment.id, fileName: r.attachment.fileName, contentType: r.attachment.contentType, size: r.attachment.size, status: r.attachment.status };
          return { id: a.localId, fileName: a.name, contentType: null, size: a.size, status: 'failed', error: r?.failed?.error || null };
        })
      }));
      setStreamingStatus(null);
    }

    sendMessageStreaming(apiMessage, userDisplayText, null, resolvedHotelId, attachmentIds);
  };

  /**
   * Run a native action as an explicit workflow.
   *
   * A workflow-backed action does NOT go through the chat stream: it posts a
   * versioned workflow ref and gets back a persisted run with cited evidence
   * and a typed proposal set. The result renders as a WorkflowRunCard, whose
   * approve/apply buttons call the workflow's own endpoints — so every write
   * carries the run's audit, apply reservation and staleness checks.
   */
  const launchWorkflow = async ({ workflowRef, label, workflowContext, hotelIdOverride }) => {
    const resolvedHotelId = hotelIdOverride || selectedHotelId;
    if (!resolvedHotelId || resolvedHotelId === 'all') {
      setError('Workflows run against a single property. Pick one property first.');
      return;
    }
    const orgId = parseInt(resolvedOrganizationId || '0', 10) || null;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: label,
      displayContent: label,
      timestamp: new Date()
    };
    const pendingId = Date.now() + 1;
    setMessages(prev => [...prev, userMessage, {
      id: pendingId,
      role: 'assistant',
      content: '',
      workflowPending: true,
      workflowPendingLabel: workflowRef === 'monthly_landing.v1'
        ? 'Calculating the month-end landing…'
        : workflowRef === 'budget_review.v1'
          ? 'Reviewing the budget against history, notes and your checklist…'
          : workflowRef === 'forecast_build.v1'
            ? 'Planning Rooms at stat-account grain and fitting drivers to every other account…'
            : 'Running the forecast accuracy review…',
      timestamp: new Date()
    }]);
    setIsLoading(true);
    turnInFlightRef.current = true;
    if (onRunStateChange) onRunStateChange(true);
    setError(null);

    try {
      const launchContext = pageContextRef.current;
      const result = await runWorkflow({
        workflowRef,
        hotelId: resolvedHotelId,
        currentOrgId: orgId,
        // A native action and a typed question send the SAME envelope, so the
        // run and the conversation agree about what the user was looking at.
        pageContext: launchContext?.envelope || null,
        versionId: workflowContext?.versionId ?? null,
        year: workflowContext?.year ?? null,
        asOf: workflowContext?.asOf ?? null,
        params: workflowContext?.params ?? null,
        question: label,
        // Every file attached on this thread (typed turns + staged chips) so
        // a button-launched review can read the same notes/checklist.
        attachmentIds: [...new Set([...threadAttachmentIdsRef.current, ...readyAttachments.map(a => a.attachment.id)])],
        threadId
      });
      // The server tells us what it actually resolved. If the row we sent is
      // gone, drop it here rather than letting the next question analyze a
      // different one.
      applyPageContextFeedback(result.pageContext || null, launchContext?.hash || null);
      setMessages(prev => prev.map(m => (m.id === pendingId
        ? { ...m, workflowPending: false, workflowRun: result, orgId, content: '', contextSnapshotLabel: result.pageContext?.label || launchContext?.label || null }
        : m)));
      if (onResponseReady) onResponseReady({
        kind: (result.proposals || []).length > 0 ? 'review' : 'response',
        threadId,
        workflowRunId: result.run?.workflowRunId || null
      });
    } catch (err) {
      const message = err?.response?.data?.error || err.message || 'The workflow could not be started.';
      setMessages(prev => prev.map(m => (m.id === pendingId
        ? { ...m, workflowPending: false, content: message }
        : m)));
      setError(message);
    } finally {
      turnInFlightRef.current = false;
      setIsLoading(false);
      if (onRunStateChange) onRunStateChange(false);
      flushPendingScopeNotice();
    }
  };

  const handlePromptClick = async (promptText, promptKey = null, options = null) => {
    if (!promptText.trim() || isLoading) return;

    // A native action that names a workflow ref launches the deterministic
    // path instead of a text prompt. Everything else is unchanged.
    if (options?.workflowRef) {
      const narrowedForWorkflow = applyScopeNarrowingIfMentioned(promptText);
      const workflowHotelId = narrowedForWorkflow ? narrowedForWorkflow.id : resolveHotelFromQuestion(promptText);
      if (contextPrefix && onContextPrefixConsumed) onContextPrefixConsumed();
      await launchWorkflow({
        workflowRef: options.workflowRef,
        label: options.label || promptText,
        workflowContext: options.workflowContext || null,
        hotelIdOverride: workflowHotelId
      });
      return;
    }

    const narrowed = applyScopeNarrowingIfMentioned(promptText);
    const resolvedHotelId = narrowed ? narrowed.id : resolveHotelFromQuestion(promptText);
    if (!resolvedHotelId) return;

    const apiMessage = contextPrefix ? `${contextPrefix}${promptText}` : promptText;
    if (contextPrefix && onContextPrefixConsumed) onContextPrefixConsumed();

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: promptText,
      displayContent: promptText,
      timestamp: new Date(),
      contextSnapshotLabel: pageContextRef.current?.label || null
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    // Only backend-implemented constellation keys are worth sending — see
    // SERVER_PROMPT_KEYS. Everything else goes through the v2 agent as a
    // plain question.
    const effectivePromptKey = promptKey && SERVER_PROMPT_KEYS.has(promptKey) ? promptKey : null;
    sendMessageStreaming(apiMessage, promptText, effectivePromptKey, resolvedHotelId);
  };

  // Native actions dispatched from a page (forecast toolbar, dashboards):
  // run them on arrival instead of prefilling the box. Deduped on the
  // action's timestamp so a re-render never re-sends a turn.
  const lastActionRef = useRef(null);
  useEffect(() => {
    if (!pendingAction?.prompt || !selectedHotelId || isLoading) return;
    const key = pendingAction.requestedAt || pendingAction.prompt;
    if (lastActionRef.current === key) return;
    lastActionRef.current = key;
    handlePromptClick(
      pendingAction.prompt,
      pendingAction.promptKey || null,
      pendingAction.workflowRef
        ? {
          workflowRef: pendingAction.workflowRef,
          label: pendingAction.label || pendingAction.prompt,
          workflowContext: pendingAction.workflowContext || null
        }
        : null
    );
    if (onPendingActionConsumed) onPendingActionConsumed();
    // handlePromptClick is redefined every render; the timestamp guard above
    // is what keeps this from firing twice, not the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction, selectedHotelId, isLoading]);

  const handleRating = async (messageIndex, rating) => {
    const message = messages[messageIndex];

    if (message.role !== 'assistant') return;

    const feedbackThreadId = message.threadId || threadId;

    if (!feedbackThreadId) {
      console.error('No threadId available for feedback submission');
      return;
    }

    setMessages(prev => prev.map((msg, idx) =>
      idx === messageIndex ? { ...msg, rating } : msg
    ));

    try {
      await submitAIChatFeedback({
        threadId: feedbackThreadId,
        hotelId: selectedHotelId || 'all',
        question: messages[messageIndex - 1]?.content || '',
        answer: message.content,
        rating: rating === 'positive' ? 'positive' : 'negative',
        messageId: message.id.toString(),
        turnId: message.turnId || null
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setMessages(prev => prev.map((msg, idx) =>
        idx === messageIndex ? { ...msg, rating: null } : msg
      ));
    }
  };

  const handleNewConversation = () => {
    // Abort any in-flight stream and drain the token buffer before resetting.
    if (tokenDrainRef.current) {
      cancelAnimationFrame(tokenDrainRef.current);
      tokenDrainRef.current = null;
    }
    tokenBufferRef.current = '';
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    turnInFlightRef.current = false;
    // A new/cleared conversation discards a deferred scope marker with it.
    pendingScopeNoticeRef.current = null;
    if (onRunStateChange) onRunStateChange(false);
    // Always clear local state. We can't rely on the parent's
    // setActiveThreadId(null) cascade — if activeThreadId was already null
    // (e.g. user just started a fresh chat and has typed one message), the
    // useEffect on activeThreadId won't fire.
    setMessages([]);
    setThreadId(null);
    setError(null);
    setInput('');
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingStatus(null);
    setPendingArtifactType(null);
    // Fast is a user preference, not a per-thread model selection; retain it.
    if (onNewConversation) onNewConversation();
  };

  const lastNewConversationRequestRef = useRef(newConversationRequest);
  useEffect(() => {
    if (!newConversationRequest || newConversationRequest === lastNewConversationRequestRef.current) return;
    lastNewConversationRequestRef.current = newConversationRequest;
    handleNewConversation();
    // handleNewConversation intentionally owns the full local reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newConversationRequest]);

  // Share the current AI session with one or more teammates via Stream Chat.
  // The actual modal + fork-and-send flow lives in aiChatShareUtils.js; we
  // just gather session metadata and call the globally-exposed entry point
  // that StreamChatBox publishes on window.
  const handleShareSession = async () => {
    const effectiveThreadId = activeThreadId || threadId;
    if (!effectiveThreadId) {
      alert('Start a conversation before sharing.');
      return;
    }
    if (typeof window.sendAIChatShareMessage !== 'function') {
      alert('Chat is still connecting. Try again in a moment.');
      return;
    }
    // Pick a preview of the most recent user message — matches how sessions
    // surface their "last message" in the history tray.
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    const sessionInfo = {
      sessionId: `session_${effectiveThreadId}`,
      threadId: effectiveThreadId,
      hotelName: selectedHotelName || (selectedHotelId === 'all' ? 'All Locations' : 'AI Conversation'),
      messageCount: messages.length,
      lastMessage: (lastUserMessage?.content || '').substring(0, 120)
    };
    try {
      await window.sendAIChatShareMessage(sessionInfo);
    } catch (err) {
      console.error('Failed to share AI chat session:', err);
    }
  };

  // Strip markdown decorations for clipboard — keeps tables as-is (they're useful
  // pasted into Sheets/Excel) but drops ```chart/pnl/forecast code fences.
  const copyMessageToClipboard = async (content) => {
    let text = content || '';
    text = text.replace(/```(?:chart|pnl_report|forecast_changes|briefing-cards)\n[\s\S]*?\n```/g, '[chart/table removed]');
    text = text.replace(/```signals\n[\s\S]*?\n```/g, '');
    text = text.replace(/```(?:ask_user|email_preview|scheduled_task_approval)\n[\s\S]*?\n```/g, '');
    try {
      await navigator.clipboard.writeText(text.trim());
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Retry: find the user message immediately before this assistant message and re-send.
  const retryMessage = (assistantIndex) => {
    if (isLoading || isStreaming) return;
    const userMsg = messages.slice(0, assistantIndex).reverse().find(m => m.role === 'user');
    if (!userMsg) return;
    // Drop the failed/undesired assistant message and any messages after it
    setMessages(prev => prev.slice(0, assistantIndex));
    const text = userMsg.displayContent || userMsg.content;
    const narrowed = applyScopeNarrowingIfMentioned(text);
    const resolvedHotelId = narrowed ? narrowed.id : resolveHotelFromQuestion(text);
    if (!resolvedHotelId) return;
    setIsLoading(true);
    setError(null);
    const apiMessage = contextPrefix ? `${contextPrefix}${text}` : text;
    sendMessageStreaming(apiMessage, text, null, resolvedHotelId);
  };

  const handleAskAboutPnLItem = (contextString) => {
    setInput(prev => {
      const prefix = prev.trim() ? prev.trim() + ' ' : '';
      return `${prefix}${contextString}\n`;
    });
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = '0';
          const newHeight = Math.min(inputRef.current.scrollHeight, 160);
          inputRef.current.style.height = newHeight + 'px';
          inputRef.current.style.overflow = inputRef.current.scrollHeight > 160 ? 'auto' : 'hidden';
        }
      }, 0);
    }
  };

  const renderMarkdown = (content, isStillStreaming = false) => {
    // Hide internal provenance accidentally persisted by older backend turns.
    // New turns no longer place this digest in model-visible history, but the
    // UI should also clean existing conversations when they are reopened.
    content = String(content || '').replace(/^\s*\[data used:[^\n]*\]\s*$/gim, '');

    // During streaming, if a briefing-cards block has started but not yet closed,
    // hide the raw JSON and render a placeholder loader instead. The fence + JSON
    // often arrive on a single line, so we can't rely on line-level matching here.
    let briefingStreaming = false;
    if (isStillStreaming && isBriefingStreaming(content)) {
      const fenceIdx = content.indexOf('```briefing-cards');
      content = content.slice(0, fenceIdx);
      briefingStreaming = true;
    }

    // Defense-in-depth against prompt drift: strip patterns the design explicitly
    // banned that the model keeps emitting anyway. The eval set's job is to push
    // fixes upstream (backend prompt); these regexes are the safety net until the
    // model is fully compliant.
    if (!isStillStreaming) {
      // 1) "Key Takeaways:" / "Key Insights:" / "Key Observations:" / "Summary:"
      //    / "Bottom Line:" / "Highlights:" in any of these forms:
      //       **Header:** body
      //       ## Header   (H2 or H3, optionally emoji-prefixed)
      //       ### Header
      //    Eats through the following bullet-list or short prose until the next
      //    H-level heading, bold paragraph, or end of content.
      const bannedSections = '(?:Key\\s+(?:Takeaways?|Insights?|Observations?|Findings?|Metrics?)|Highlights?|Summary|Bottom\\s+Line|The\\s+Wins?|The\\s+Misses?)';
      // Bold-paragraph form: **Key Insights:**
      content = content.replace(
        new RegExp(`\\n\\n\\*\\*${bannedSections}:?\\*\\*[\\s\\S]*?(?=\\n\\n\\*\\*[A-Z]|\\n\\n#{1,3}\\s|\\n\\n\`\`\`|$)`, 'g'),
        ''
      );
      // H2/H3 heading form: ## Key Insights  /  ### 📊 Key Insights
      content = content.replace(
        new RegExp(`\\n\\n#{2,3}\\s*(?:[\\p{Emoji_Presentation}\\p{Extended_Pictographic}]\\s*)?${bannedSections}[\\s\\S]*?(?=\\n\\n#{1,3}\\s|\\n\\n\`\`\`|$)`, 'gu'),
        ''
      );
      // Plain-label form: "Key Insights:\n- …"
      content = content.replace(
        new RegExp(`\\n\\n${bannedSections}:?\\s*\\n(?:\\s*[-•*]\\s.+\\n?)+`, 'g'),
        ''
      );
      // 2) "**Chart Title:**" bold headers directly before a ```chart block —
      //    duplicates the chart's own title field.
      content = content.replace(
        /\n\n\*\*[^*\n]{3,80}:\*\*\s*\n+(?=```chart\n)/g,
        '\n\n'
      );
      // 3) Greeting tail: "Based on your current data, a few things stand out:"
      //    / "Here's what I noticed:" / "A few things worth flagging:" followed
      //    by bullets. These appear on conversational inputs where the user
      //    didn't ask for insights.
      content = content.replace(
        /\n\n(?:\*\*)?(?:Based on your (?:current )?data|Here's what I noticed|A few things (?:stand out|worth flagging|you may want)|Things that (?:stand out|caught my eye))[^\n]*(?:\*\*)?:?\s*\n(?:\s*[-•*][^\n]+\n?)+/gi,
        ''
      );
      // 4) Trim any leading H1 that just echoes the response title.
      //    "# Last Month's P&L with Budget Variances" above a P&L is noise.
      content = content.replace(/^#\s+[^\n]+\n+/, '');
      // 5) Duplicate table after a ```chart block — the chart's own Chart/Table
      //    toggle already exposes the rows. Strip the redundant markdown table
      //    that immediately follows a chart block (up to two blank lines of gap).
      //    Conservative: only strip tables with ≤12 data rows to avoid eating a
      //    wide secondary table that happens to follow a chart coincidentally.
      content = content.replace(
        /(```chart\n[\s\S]*?\n```)\s*\n+(\|[^\n]+\|\n\|[\s:-]+\|\n(?:\|[^\n]+\|\n?){1,13})/g,
        '$1\n'
      );
      // 6) "Here's the full breakdown:" (or similar) prose lead-in to a table
      //    that follows a chart — strip the lead-in too.
      content = content.replace(
        /(```chart\n[\s\S]*?\n```)\s*\n+(?:Here's the [^\n]+breakdown[^\n]*|Full breakdown[^\n]*|Details:?)[:.]?\s*\n/gi,
        '$1\n'
      );
    }

    // 7) Normalize glued fences — the line-by-line parser below requires
    //    ```chart / ```pnl_report / ```forecast_changes / ```briefing-cards
    //    to sit on their own line, but models occasionally emit the opening
    //    fence glued to the end of a prose sentence (e.g. "daily averages.```chart\n{json}")
    //    or the closing ``` glued to the last JSON character (`}```). Force
    //    a newline on both sides so the parser recognizes the block.
    //    Opening: insert \n before ```<blocktype> when preceded by any non-newline char.
    content = content.replace(
      /([^\n])(```(?:chart|signals|pnl_report|forecast_changes|briefing-cards|ask_user|email_preview|scheduled_task_approval)\b)/g,
      '$1\n$2'
    );
    //    Closing: insert \n before ``` when glued to a closing } ] or quote
    //    (the last char of a JSON payload).
    content = content.replace(
      /([}\]"])\s*(```)(?=\s*(?:\n|$))/g,
      '$1\n$2'
    );

    // ── Markdown → typed blocks ─────────────────────────────────────────
    // The loop below no longer emits React elements directly. It emits typed
    // blocks ({kind, text, el, …}) that ResponseBody lays out in the AI
    // Response Pattern order (headline · caveats · signals · lead · sections
    // · actions). See response/layoutResponse.js for the block shapes.
    const lines = content.split('\n');
    const blocks = [];
    const push = (b) => { if (b) blocks.push(b); };
    let inTable = false;
    let tableRows = [];
    let openFence = null;   // { type, lines, idx }
    let listBuf = null;     // { ordered, items: [{text, el}] }

    const flushList = () => {
      if (listBuf && listBuf.items.length) push({ kind: 'list', ordered: listBuf.ordered, items: listBuf.items });
      listBuf = null;
    };
    const flushTable = () => {
      if (inTable && tableRows.length) {
        push({ kind: 'table', rows: tableRows, el: createTableElement(tableRows, `table-${blocks.length}`) });
      }
      inTable = false;
      tableRows = [];
    };
    const flushInline = () => { flushList(); flushTable(); };

    const createTableElement = (rows, key) => {
      const tableMarkdown = rows.join('\n');

      const headerRow = rows[0];
      const dataRows = [];
      const totalRows = [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.includes('---')) continue;

        const cells = row.split('|').filter(cell => cell.trim());
        const isTotalRow = cells[0] && cells[0].trim().toUpperCase().includes('TOTAL');

        if (isTotalRow) {
          totalRows.push(row);
        } else {
          dataRows.push(row);
        }
      }

      const headerCells = headerRow.split('|').filter(cell => cell.trim()).map(h => h.trim().replace(/\*\*/g, ''));
      const headerCount = headerCells.length;

      // Detect which columns are numeric by scanning data rows
      const numericCols = new Set();
      const currencyOrNumRegex = /^\s*[+-]?\$?[\d,]+\.?\d*%?\s*$/;
      for (const dr of dataRows) {
        const dCells = dr.split('|').filter(c => c.trim());
        dCells.forEach((c, i) => {
          if (currencyOrNumRegex.test(c.trim().replace(/\*\*/g, ''))) numericCols.add(i);
        });
      }

      // ----- Try to promote to a chart (design-first rendering) -----
      // A table is "chartable" when:
      //   - 2 columns
      //   - ≥ 2 data rows, ≤ 20 data rows
      //   - First column is categorical (not numeric)
      //   - Exactly one numeric metric column. Multi-metric tables stay as
      //     tables because promoting them to a single-series chart hides data
      //     and creates a confusing Chart/Table toggle.
      const firstColIsCategory = !numericCols.has(0);
      const numericAfterFirst = [...numericCols].filter(i => i > 0);
      const chartable =
        headerCount === 2 &&
        dataRows.length >= 2 &&
        dataRows.length <= 20 &&
        firstColIsCategory &&
        numericAfterFirst.length === 1;

      if (chartable) {
        const parseNum = (s) => {
          const t = String(s || '').trim().replace(/\*\*/g, '').replace(/[$,]/g, '');
          const pct = /%$/.test(t);
          const n = parseFloat(pct ? t.replace('%', '') : t);
          return Number.isFinite(n) ? n : null;
        };
        const parsed = dataRows.map(r => {
          const c = r.split('|').filter(x => x.trim()).map(x => x.trim().replace(/\*\*/g, ''));
          const obj = {};
          headerCells.forEach((h, i) => {
            obj[h] = numericCols.has(i) ? (parseNum(c[i]) ?? c[i]) : (c[i] || '');
          });
          return obj;
        });
        // Sort descending by the primary numeric column so the chart reads top-to-bottom
        const primaryNumKey = headerCells[numericAfterFirst[0]];
        parsed.sort((a, b) => (typeof b[primaryNumKey] === 'number' ? b[primaryNumKey] : 0)
          - (typeof a[primaryNumKey] === 'number' ? a[primaryNumKey] : 0));

        // Infer currency vs number from the header text + values
        const hdrLower = (primaryNumKey || '').toLowerCase();
        const looksCurrency = /\$|revenue|cost|expense|spend|sales|income|adr/.test(hdrLower)
          || dataRows.some(r => /\$/.test(r));
        const looksPct = /%|percent|pct|share/.test(hdrLower);
        const yAxisType = looksPct ? 'percentage' : looksCurrency ? 'currency' : 'number';

        const chartConfig = {
          type: 'bar',
          data: parsed,
          xKey: headerCells[0],
          yKeys: [primaryNumKey],
          yAxisType,
        };
        return <ChartRenderer key={key} chartConfig={chartConfig} />;
      }

      return (
        <ExpandableAIChatTable
          key={key}
          headerRow={headerRow}
          dataRows={dataRows}
          totalRows={totalRows}
          headerCount={headerCount}
          numericCols={numericCols}
          tableMarkdown={tableMarkdown}
          hotelName={selectedHotelName || 'Hotel'}
          isDarkMode={isDarkMode}
        />
      );
    };

    // Format inline markdown: **bold**, [links](url), and bare URLs
    const formatInline = (text) => {
      const parts = [];
      let remaining = text;
      let partKey = 0;

      while (remaining.length > 0) {
        // Find the earliest match of any inline pattern
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        const linkMatch = remaining.match(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/);
        const bareUrlMatch = remaining.match(/(^|[\s(])((https?:\/\/)[^\s)<]+)/);

        // Determine which match comes first
        const matches = [];
        if (boldMatch) matches.push({ type: 'bold', index: boldMatch.index, match: boldMatch });
        if (linkMatch) matches.push({ type: 'link', index: linkMatch.index, match: linkMatch });
        if (bareUrlMatch) matches.push({ type: 'url', index: bareUrlMatch.index + bareUrlMatch[1].length, match: bareUrlMatch });

        if (matches.length === 0) {
          parts.push(remaining);
          break;
        }

        // Sort by position, pick earliest
        matches.sort((a, b) => a.index - b.index);
        const earliest = matches[0];

        if (earliest.type === 'bold') {
          const before = remaining.slice(0, earliest.match.index);
          if (before) parts.push(before);
          parts.push(<strong key={`b-${partKey++}`}>{earliest.match[1]}</strong>);
          remaining = remaining.slice(earliest.match.index + earliest.match[0].length);
        } else if (earliest.type === 'link') {
          const before = remaining.slice(0, earliest.match.index);
          if (before) parts.push(before);
          parts.push(
            <a key={`l-${partKey++}`} href={earliest.match[2]} target="_blank" rel="noopener noreferrer" className="ai-chat-link">
              {earliest.match[1]} <span className="link-icon">↗</span>
            </a>
          );
          remaining = remaining.slice(earliest.match.index + earliest.match[0].length);
        } else if (earliest.type === 'url') {
          // Include any prefix whitespace/paren before the URL
          const before = remaining.slice(0, earliest.index);
          if (before) parts.push(before);
          const url = earliest.match[2];
          // Show shortened domain as label
          let label;
          try {
            label = new URL(url).hostname.replace('www.', '');
          } catch {
            label = url.length > 40 ? url.substring(0, 40) + '...' : url;
          }
          parts.push(
            <a key={`u-${partKey++}`} href={url} target="_blank" rel="noopener noreferrer" className="ai-chat-link">
              {label} <span className="link-icon">↗</span>
            </a>
          );
          remaining = remaining.slice(earliest.index + url.length);
        }
      }

      return parts;
    };

    const parseJsonFence = (fenceLines) => JSON.parse(fenceLines.join('\n'));

    // One closed fence → one block. Unknown fence languages render as code.
    const fenceBlock = (type, fenceLines, idx) => {
      const fail = (label) => ({ kind: 'p', text: label, el: <p key={`fence-error-${idx}`} className="chart-parse-error">{label}</p> });
      switch (type) {
        case 'chart': {
          try {
            const chartConfig = parseJsonFence(fenceLines);
            return { kind: 'chart', el: <ChartRenderer key={`chart-${idx}`} chartConfig={chartConfig} /> };
          } catch (error) {
            console.error('Error parsing chart JSON:', error);
            return fail('Invalid chart configuration');
          }
        }
        case 'signals': {
          try {
            const signals = normalizeSignals(parseJsonFence(fenceLines));
            return signals.length ? { kind: 'signals', signals } : null;
          } catch (error) {
            console.error('Error parsing signals JSON:', error);
            return null;
          }
        }
        case 'briefing-cards': {
          try {
            const briefingData = parseJsonFence(fenceLines);
            return {
              kind: 'briefing',
              el: (
                <BriefingCardsRenderer
                  key={`briefing-${idx}`}
                  data={briefingData}
                  onPromptClick={(prompt) => prompt && handlePromptClick(prompt)}
                  hotelName={selectedHotelName || 'Portfolio'}
                  isDarkMode={isDarkMode}
                />
              ),
            };
          } catch (error) {
            console.error('Error parsing briefing JSON:', error);
            return fail('Invalid briefing data');
          }
        }
        case 'forecast_changes': {
          try {
            const proposal = parseJsonFence(fenceLines);
            return { kind: 'forecast', el: <ForecastChangeCard key={`forecast-${idx}`} proposal={proposal} hotelId={selectedHotelId} orgId={resolvedOrganizationId} /> };
          } catch (error) {
            console.error('Error parsing forecast changes JSON:', error);
            return fail('Invalid forecast change data');
          }
        }
        case 'email_preview': {
          try {
            const previewData = parseJsonFence(fenceLines);
            return { kind: 'email_preview', el: <EmailPreviewCard key={`email-preview-${idx}`} data={previewData} onAnswer={(reply) => reply && handlePromptClick(reply)} /> };
          } catch (error) {
            console.error('Error parsing email_preview JSON:', error);
            return null;
          }
        }
        case 'scheduled_task_approval': {
          try {
            const approvalData = parseJsonFence(fenceLines);
            return { kind: 'task_approval', el: <ScheduledTaskApprovalCard key={`task-approval-${idx}`} data={approvalData} onAnswer={(reply) => reply && handlePromptClick(reply)} /> };
          } catch (error) {
            console.error('Error parsing scheduled_task_approval JSON:', error);
            return null;
          }
        }
        case 'ask_user': {
          try {
            const askData = parseJsonFence(fenceLines);
            return { kind: 'ask', el: <QuestionCard key={`ask-${idx}`} data={askData} onAnswer={(reply) => reply && handlePromptClick(reply)} /> };
          } catch (error) {
            console.error('Error parsing ask_user JSON:', error);
            return null;
          }
        }
        case 'pnl_report': {
          try {
            const pnlData = parseJsonFence(fenceLines);
            return {
              kind: 'pnl',
              el: (
                <PnLReportTable
                  key={`pnl-${idx}`}
                  data={pnlData}
                  hotelName={selectedHotelName || 'Hotel'}
                  isDarkMode={isDarkMode}
                  onAskAbout={handleAskAboutPnLItem}
                  orgId={resolvedOrganizationId}
                  hotelId={selectedHotelId}
                />
              ),
            };
          } catch (error) {
            console.error('Error parsing P&L report JSON:', error);
            return fail('Invalid P&L report data');
          }
        }
        case 'financial_analyst': {
          try {
            const faData = parseJsonFence(fenceLines);
            return {
              kind: 'fa',
              el: (
                <FinancialAnalystResponse
                  key={`fa-${idx}`}
                  packageId={faData.packageId}
                  generateConfig={faData.generateConfig}
                  locations={locations || []}
                  currentOrgId={resolvedOrganizationId}
                />
              ),
            };
          } catch (error) {
            console.error('Error parsing financial analyst JSON:', error);
            return fail('Invalid financial analyst data');
          }
        }
        default: {
          const code = fenceLines.join('\n');
          if (!code.trim()) return null;
          return { kind: 'code', el: <pre key={`code-${idx}`} className="ai-code-block">{code}</pre> };
        }
      }
    };

    lines.forEach((line, idx) => {
      const trimmedLine = line.trim();

      // Fences (```chart, ```signals, ```pnl_report, … and plain code)
      if (openFence) {
        if (trimmedLine === '```') {
          push(fenceBlock(openFence.type, openFence.lines, openFence.idx));
          openFence = null;
        } else {
          openFence.lines.push(line);
        }
        return;
      }
      const fenceOpen = trimmedLine.match(/^```([\w-]*)\s*$/);
      if (fenceOpen) {
        flushInline();
        openFence = { type: (fenceOpen[1] || 'code').toLowerCase(), lines: [], idx };
        return;
      }

      // Markdown tables
      if (trimmedLine.startsWith('|')) {
        flushList();
        if (!inTable) { inTable = true; tableRows = []; }
        tableRows.push(line);
        return;
      }
      if (inTable) flushTable();

      if (!trimmedLine || /^(?:-{3,}|\*{3,}|_{3,})$/.test(trimmedLine)) {
        // Blank line or horizontal rule ends a list.
        flushList();
        return;
      }

      // Headings
      const hMatch = trimmedLine.match(/^(#{1,4})\s+(.+)/);
      if (hMatch) {
        flushList();
        const level = hMatch[1].length;
        const text = hMatch[2].trim();
        const cls = level === 1 ? 'md-h1' : level === 2 ? 'md-h2' : 'md-h3';
        const Tag = level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4';
        push({ kind: 'heading', level, text, el: <Tag key={`h-${idx}`} className={`md-heading ${cls}`}>{formatInline(text)}</Tag> });
        return;
      }

      // Caveats: "> ⚠ …" blockquotes, or a line that opens with a warning glyph.
      const quote = trimmedLine.match(/^>\s?(.*)$/) || trimmedLine.match(/^(?:⚠️|⚠|ℹ️|ℹ)\s*(.*)$/);
      if (quote) {
        flushList();
        const raw = quote[1] || '';
        const tone = /^(?:ℹ️|ℹ)/.test(trimmedLine) || /^(?:ℹ️|ℹ)/.test(raw) ? 'info' : 'warning';
        const text = raw.replace(/^(?:⚠️|⚠|ℹ️|ℹ)\s*/, '').replace(/^\*\*(?:note|caveat|warning|heads[- ]up)[:.]?\*\*\s*/i, '').trim();
        if (text) push({ kind: 'caveat', tone, text, el: <React.Fragment key={`q-${idx}`}>{formatInline(text)}</React.Fragment> });
        return;
      }

      // Lists
      const ul = line.match(/^\s*[-*•]\s+(.+)$/);
      const ol = line.match(/^\s*(\d{1,3})[.)]\s+(.+)$/);
      if (ul || ol) {
        const ordered = !!ol;
        const text = (ol ? ol[2] : ul[1]).trim();
        if (!listBuf || listBuf.ordered !== ordered) { flushList(); listBuf = { ordered, items: [] }; }
        listBuf.items.push({ text, el: <React.Fragment key={`li-${idx}`}>{formatInline(text)}</React.Fragment> });
        return;
      }
      // Indented continuation of the previous list item
      if (listBuf && /^\s{2,}\S/.test(line)) {
        const last = listBuf.items[listBuf.items.length - 1];
        last.text = `${last.text} ${trimmedLine}`;
        last.el = <React.Fragment key={`li-${idx}`}>{formatInline(last.text)}</React.Fragment>;
        return;
      }
      flushList();

      // Source-attribution footers ("[Google Hotels](…) · observed …") get a
      // muted small-print treatment — purely presentational.
      const isAttribution = /^\[[^\]]+\]\([^)]+\)\s*·/.test(trimmedLine);
      push({
        kind: 'p',
        text: trimmedLine,
        attribution: isAttribution,
        inline: formatInline(trimmedLine),
        el: <p key={`p-${idx}`} className={isAttribution ? 'ai-attribution' : undefined}>{formatInline(trimmedLine)}</p>,
      });
    });

    flushList();

    if (briefingStreaming) {
      push({
        kind: 'briefing-loading',
        el: (
          <div key="briefing-loading" className="briefing-cards-container" style={{ padding: '24px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#9ca3af', fontSize: 13 }}>
              <div className="thinking-orb-inline" style={{ width: 20, height: 20 }}>
                <div className="orb-ring" />
                <div className="orb-ring2" />
                <div className="orb-core" />
              </div>
              Building your daily briefing…
            </div>
          </div>
        ),
      });
    }

    if (inTable && tableRows.length > 0) {
      if (isStillStreaming) {
        // Table is still being streamed — show header with loading spinner
        const headerRow = tableRows[0];
        const headerCells = headerRow.split('|').filter(cell => cell.trim());
        push({
          kind: 'table-loading',
          el: (
            <div key="table-loading" className="ai-chat-table-container" style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', marginBottom: 8 }}>
              <table className="ai-chat-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {headerCells.map((cell, i) => (
                      <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, borderBottom: '2px solid rgba(0,0,0,0.12)' }}>
                        {cell.trim().replace(/\*\*/g, '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={headerCells.length} style={{ padding: '20px 0', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#9ca3af', fontSize: 13 }}>
                        <div className="thinking-orb-inline" style={{ width: 18, height: 18 }}>
                          <div className="orb-ring" />
                          <div className="orb-ring2" />
                          <div className="orb-core" />
                        </div>
                        Loading data...
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ),
        });
      } else {
        flushTable();
      }
    }

    if (openFence && !isStillStreaming) {
      // Unterminated fence in a finished answer: render what we have.
      push(fenceBlock(openFence.type, openFence.lines, openFence.idx));
    }
    // An open fence mid-stream renders nothing: the skeleton placeholders
    // ("Assembling…") were removed from responses — the block appears whole
    // when its closing fence arrives.

    if (blocks.length === 0) return <p>{content}</p>;
    return <ResponseBody blocks={blocks} streaming={isStillStreaming} inline={formatInline} />;
  };

  const hasMessages = messages.length > 0 || isLoadingHistory;

  const renderInputForm = () => (
    <form className={`ai-chat-input-form ${!hasMessages && !compact ? 'centered' : ''}`} onSubmit={handleSendMessage}>
      {!hasMessages && !compact && (
        <div className="centered-branding">
          <LodgeicAIIcon size={40} withBackground={true} />
          <h2 className="centered-title">Lodgeic AI</h2>
          <p className="centered-subtitle">
            {locations?.length > 1
              ? 'Ask about any of your properties by name, or ask a question across all locations.'
              : 'Ask questions about your property data, forecasts, revenue trends, and more.'
            }
          </p>
        </div>
      )}
      {/* What the assistant will actually be told about the current page. Says
          precisely which property/version/period/selection — never "it sees
          everything on this page". */}
      {pageContextSnapshot?.label && (
        <AssistantContextChip
          label={pageContextSnapshot.label}
          warning={pageContextWarning}
          canClearSelection={!!pageContextSnapshot.envelope?.selection}
          onClearSelection={clearPageSelection}
        />
      )}
      {renderPendingAttachments()}
      <div
        className={`ai-chat-input-wrapper${isDragOver ? ' is-dragover' : ''}`}
        onDrop={handleComposerDrop}
        onDragOver={handleComposerDragOver}
        onDragLeave={handleComposerDragLeave}
      >
        {/* Plus menu — fan out icons, then open detail panel (hidden in compact/forecast mode) */}
        {!compact && <div className="plus-menu-container" ref={plusMenuRef}>
          <button
            type="button"
            className={`plus-menu-trigger ${showPlusMenu ? 'open' : ''}`}
            onClick={() => {
              if (showPlusMenu) {
                setShowPlusMenu(false);
                setActiveMenuSection(null);
              } else {
                setShowPlusMenu(true);
                setActiveMenuSection(null);
              }
            }}
            title="More options"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>

          {/* Fan-out action pills */}
          {showPlusMenu && !activeMenuSection && (
            <div className="fan-menu">
              <button type="button" className="fan-item" style={{ '--fan-index': 0 }}
                onClick={() => { setActiveMenuSection('location'); setLocationFilter(''); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <span>Properties</span>
              </button>
              <button type="button" className="fan-item" style={{ '--fan-index': 1 }}
                onClick={() => setActiveMenuSection('prompts')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26z"/>
                </svg>
                <span>Prompts</span>
              </button>
              <button type="button" className="fan-item" style={{ '--fan-index': 2 }}
                onClick={() => setActiveMenuSection('history')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                </svg>
                <span>History</span>
              </button>
              <button type="button" className="fan-item" style={{ '--fan-index': 3 }}
                onClick={() => { handleNewConversation(); setShowPlusMenu(false); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M12 7v6M9 10h6"/>
                </svg>
                <span>New Chat</span>
              </button>
            </div>
          )}

          {/* Tool panels — opens when a fan icon is clicked */}
          {showPlusMenu && activeMenuSection && (
            <div className="tool-panel">
              {/* ── Location: Multi-Select Chip Picker ── */}
              {activeMenuSection === 'location' && (
                <>
                  <div className="tool-panel-header">
                    <div className="tool-panel-header-left">
                      <div className="tool-panel-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      </div>
                      <span className="tool-panel-title">Select Properties</span>
                    </div>
                    <div className="tool-panel-header-right">
                      {selectedLocationIds.size > 0 && (
                        <span className="tool-panel-count">{selectedLocationIds.size} selected</span>
                      )}
                      <button type="button" className="tool-panel-close" onClick={() => setActiveMenuSection(null)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </button>
                    </div>
                  </div>
                  <div className="tool-panel-body">
                    {locations?.length > 3 && (
                      <div className="chip-filter">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                        <input
                          type="text"
                          placeholder="Filter properties..."
                          value={locationFilter}
                          onChange={(e) => setLocationFilter(e.target.value)}
                        />
                      </div>
                    )}
                    <div className="chip-grid">
                      {locations?.filter(loc =>
                        !locationFilter || loc.name.toLowerCase().includes(locationFilter.toLowerCase())
                      ).map(loc => {
                        const id = loc.id.toString();
                        const isSelected = selectedLocationIds.has(id);
                        return (
                          <button
                            key={loc.id}
                            type="button"
                            className={`property-chip ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedLocationIds(prev => {
                                const next = new Set(prev);
                                if (next.has(id)) next.delete(id); else next.add(id);
                                return next;
                              });
                            }}
                          >
                            <span className={`chip-checkbox ${isSelected ? 'checked' : ''}`}>
                              {isSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                            </span>
                            <span className="chip-name">{loc.name}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="chip-actions">
                      <button
                        type="button"
                        className="chip-apply-btn"
                        onClick={() => {
                          if (selectedLocationIds.size === 1) {
                            const id = [...selectedLocationIds][0];
                            if (onHotelChange) onHotelChange(id);
                          } else if (selectedLocationIds.size > 1) {
                            if (onHotelChange) onHotelChange('all');
                          }
                          setShowPlusMenu(false);
                          setActiveMenuSection(null);
                        }}
                      >
                        Apply Selection
                      </button>
                      <button
                        type="button"
                        className="chip-clear-btn"
                        onClick={() => {
                          const allIds = new Set(locations.map(l => l.id.toString()));
                          if (selectedLocationIds.size === locations?.length) {
                            setSelectedLocationIds(new Set());
                          } else {
                            setSelectedLocationIds(allIds);
                          }
                        }}
                      >
                        {selectedLocationIds.size === locations?.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── Suggested Prompts ── */}
              {activeMenuSection === 'prompts' && (
                <>
                  <div className="tool-panel-header">
                    <div className="tool-panel-header-left">
                      <div className="tool-panel-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26z"/></svg>
                      </div>
                      <span className="tool-panel-title">Suggested Prompts</span>
                    </div>
                    <button type="button" className="tool-panel-close" onClick={() => setActiveMenuSection(null)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  </div>
                  <div className="tool-panel-body">
                    {CONSTELLATION_PROMPTS.map(prompt => (
                      <button
                        key={prompt.key}
                        type="button"
                        className="prompt-card"
                        disabled={!highlightPrompts?.[prompt.key]}
                        onClick={() => {
                          if (highlightPrompts?.[prompt.key]) {
                            handlePromptClick(highlightPrompts[prompt.key], prompt.key);
                            setShowPlusMenu(false);
                            setActiveMenuSection(null);
                          }
                        }}
                      >
                        <div className="prompt-card-icon">
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: prompt.svgIcon }} />
                        </div>
                        <div className="prompt-card-text">
                          <span className="prompt-card-label">{prompt.label}</span>
                          <span className="prompt-card-desc">{prompt.desc}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* ── History ── */}
              {activeMenuSection === 'history' && (
                <>
                  <div className="tool-panel-header">
                    <div className="tool-panel-header-left">
                      <div className="tool-panel-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                      </div>
                      <span className="tool-panel-title">Recent Conversations</span>
                    </div>
                    <button type="button" className="tool-panel-close" onClick={() => setActiveMenuSection(null)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  </div>
                  <div className="tool-panel-body">
                    {chatSessions?.length > 0 ? (
                      <div className="plus-menu-sessions">
                        {chatSessions.slice(0, 10).map(session => (
                          <div
                            key={session.threadId}
                            className={`plus-menu-session-item ${activeThreadId === session.threadId ? 'active' : ''}`}
                            onClick={() => { if (onSelectSession) onSelectSession(session.threadId); setShowPlusMenu(false); setActiveMenuSection(null); }}
                          >
                            <div className="plus-menu-session-text">
                              <span className="plus-menu-session-msg">{session.lastMessage}</span>
                              <span className="plus-menu-session-meta">
                                {session.hotelName && <span className="session-hotel-tag">{session.hotelName}</span>}
                                {session.messageCount} msgs &middot; {new Date(session.lastUpdated).toLocaleDateString()}
                              </span>
                            </div>
                            <button type="button" className="plus-menu-session-delete" onClick={(e) => { e.stopPropagation(); if (onDeleteSession) onDeleteSession(session.threadId); }} title="Delete">
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="plus-menu-empty">No conversations yet</div>
                    )}
                  </div>
                </>
              )}

            </div>
          )}
        </div>}

        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!isLoading && !isStreaming && canSend) {
                handleSendMessage(e);
              }
            }
          }}
          onPaste={handleComposerPaste}
          placeholder={compact ? "Ask about this forecast..." : (locations?.length || selectedHotelId ? "Message Lodgeic AI..." : "Loading properties...")}
          disabled={!locations?.length && !selectedHotelId}
          className="ai-chat-input"
          rows={1}
          style={{
            minHeight: '22px',
            maxHeight: '160px',
            overflow: 'hidden',
          }}
          onInput={(e) => {
            const el = e.target;
            el.style.height = '0';
            const newHeight = Math.min(el.scrollHeight, 160);
            el.style.height = newHeight + 'px';
            el.style.overflow = el.scrollHeight > 160 ? 'auto' : 'hidden';
          }}
        />

        {!compact && (() => {
          const badgeText = getLocationBadgeText();
          return badgeText ? (
            <button
              type="button"
              className="active-location-badge active-location-badge-clickable"
              title={`Context: ${badgeText} — Click to change`}
              onClick={() => {
                setShowPlusMenu(true);
                setActiveMenuSection('location');
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {badgeText}
            </button>
          ) : null;
        })()}

        {renderAttachmentButton('ai-chat-attach-btn')}
        {isGenerating ? (
          <button
            type="button"
            onClick={stopGeneration}
            className="ai-chat-send-btn is-stop"
            title="Stop generating"
            aria-label="Stop generating"
          >
            <IconStop size={12} />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSend || (!locations?.length && !selectedHotelId)}
            className="ai-chat-send-btn"
            title="Send message (Enter)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        )}
      </div>
      {!compact && (
        <div className="ai-chat-input-footer">
          <span>AI responses can include mistakes. Please double-check important figures.</span>
        </div>
      )}
    </form>
  );

  // ----- v2 helpers (used only when !compact) -----
  const v2HasLocation = !!selectedHotelId || (locations?.length > 0);
  const v2TogglePropId = (id) => {
    setSelectedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const v2SelectAll = () => setSelectedLocationIds(new Set((locations || []).map(l => l.id.toString())));
  const v2Clear = () => setSelectedLocationIds(new Set());
  const v2ApplyProperties = () => {
    if (selectedLocationIds.size === 1) {
      const id = [...selectedLocationIds][0];
      if (onHotelChange) onHotelChange(id);
    } else if (selectedLocationIds.size > 1) {
      if (onHotelChange) onHotelChange('all');
    } else if (selectedLocationIds.size === 0 && onHotelChange) {
      onHotelChange(null);
    }
    setV2Popover(null);
  };
  const v2OnPickPrompt = (promptText, promptKey) => {
    // Reuse existing prompt-click handler which sends the message with the proper promptKey.
    handlePromptClick(promptText, promptKey);
  };
  const v2SetSetting = (k, v) => setAssistantSettings(s => ({ ...s, [k]: v }));

  const v2PropertyLabel = (() => {
    if (selectedLocationIds.size === 1) {
      const id = [...selectedLocationIds][0];
      const loc = locations?.find(l => l.id.toString() === id);
      return loc?.name?.split(' ').slice(0, 3).join(' ') || id;
    }
    if (selectedLocationIds.size > 1) return `${selectedLocationIds.size} properties`;
    if (selectedHotelId === 'all') return 'All Locations';
    if (selectedHotelName) return selectedHotelName.split(' ').slice(0, 3).join(' ');
    if (locations?.length === 1) return locations[0].name.split(' ').slice(0, 3).join(' ');
    if (locations?.length > 1) return 'All Locations';
    return 'Select property';
  })();

  // Shared message-list renderer — used by both compact and v2 layouts.
  const renderMessagesList = () => (
    <>
      {isLoadingHistory ? (
        <div className="loading-history">
          <div className="loading-spinner-large"></div>
          <p>Loading conversation...</p>
        </div>
      ) : (
        messages.map((message, index) => {
          const isActiveStreamingMsg = message.isStreaming && message.id === streamingMessageIdRef.current;
          if (isActiveStreamingMsg && !message.content) return null;

          // Stream prose + tool blocks together. renderMarkdown already swaps
          // open-but-unclosed fenced blocks (```chart / ```pnl_report /
          // ```forecast_changes / ```briefing-cards) for shape-matched
          // ChartPlaceholder skeletons, so the reading experience is: text
          // streams in, skeleton appears where the chart is coming, skeleton
          // swaps to the rendered chart when the closing fence arrives, text
          // continues. Any residual tool-dialogue leak still in the stream is
          // handled by the backend's pre-stream filter in chatCompletion().
          const displayContent = message.content;
          const hasVisibleStreamContent = !!message.content;
          const allDataTables = Array.isArray(message.dataTables) ? message.dataTables : [];
          // Tool tables are supporting analysis. The answer gets one visible
          // artifact; older persisted turns with multiple tool tables are
          // cleaned up here too. Competitor rate grids are the sole exception
          // because they complement the model's position chart.
          const visibleDataTables = contentHasDataArtifact(message.content)
            ? allDataTables.filter(table => table?.toolName === 'get_competitor_rates').slice(-1)
            : allDataTables.slice(-1);
          // The message row shrink-wraps its content by default, which would
          // collapse a fluid-width run card. Rows that host one stretch instead
          // so the card tracks the real chat/panel width at any breakpoint.
          const hostsRunCard = message.role === 'assistant'
            && ((isActiveStreamingMsg && !!liveTrace)
              || (!isActiveStreamingMsg && Array.isArray(message.trace) && message.trace.length > 0));

          return (
            <div
              key={message.id}
              className={`ai-chat-message ${message.role}${hostsRunCard ? ' has-run-card' : ''}`}
            >
              <div className="message-avatar">
                {message.role === 'user' ? (
                  <svg viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                  </svg>
                ) : message.role === 'error' ? (
                  <svg viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                ) : (
                  <LodgeicAIIcon size={16} />
                )}
              </div>
              <div className="message-content">
                {/* Finished turn: one pill, expandable to a plain recap. */}
                {message.role === 'assistant' && !isActiveStreamingMsg && Array.isArray(message.trace) && message.trace.length > 0 && (
                  <RunCard done steps={message.trace} durationMs={message.traceDurationMs || 0} />
                )}

                {/* In-flight turn — ALL loading states live inside this card
                    (no standalone orbs). Reports progress as four plain-language
                    steps; no tool names and no model reasoning. */}
                {isActiveStreamingMsg && liveTrace && (
                  <RunCard
                    steps={liveTrace.steps}
                    status={streamingStatus || ''}
                    writing={liveTrace.phase === 'writing'}
                    startedAt={liveTrace.startedAt}
                    scopeLabel={runScopeLabel}
                    onStop={stopGeneration}
                  />
                )}

                {hasVisibleStreamContent && !message.workflowRun && (
                  <div className="message-text">
                    {message.role === 'assistant' ? renderMarkdown(displayContent, isActiveStreamingMsg) : message.content}
                  </div>
                )}


                {message.role === 'assistant' && Array.isArray(message.pnlReports) && message.pnlReports.map((pnl, i) => (
                  <PnLReportTable
                    key={`pnl-artifact-${message.id}-${i}`}
                    data={pnl}
                    orgId={resolvedOrganizationId}
                    hotelId={selectedHotelId}
                  />
                ))}

                {message.role === 'assistant' && message.webSearchSources?.length > 0 && (
                  <div className="web-sources">
                    <span className="web-sources-label">Sources</span>
                    <div className="web-sources-list">
                      {message.webSearchSources
                        .filter((src, i, arr) => arr.findIndex(s => s.url === src.url) === i)
                        .slice(0, 5)
                        .map((src, i) => (
                          <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="web-source-link" title={src.url}>
                            {src.title || new URL(src.url).hostname.replace('www.', '')}
                          </a>
                        ))
                      }
                    </div>
                  </div>
                )}

                {message.contextSnapshotLabel && !message.workflowRun && (
                  <AssistantContextChip label={message.contextSnapshotLabel} isSnapshot />
                )}
                {message.role === 'assistant' && <AnalysisContextChip state={message.analysisState} basis={message.dataBasis} />}

                {message.role === 'assistant' && message.workflowPending && (
                  <div className="ai-workflow-pending">{message.workflowPendingLabel || 'Running workflow…'}</div>
                )}

                {message.role === 'assistant' && message.workflowRun && (
                  <WorkflowRunCard
                    initialRun={message.workflowRun}
                    currentOrgId={message.orgId || message.workflowRun?.run?.orgId || resolvedOrganizationId}
                    contextLabel={message.contextSnapshotLabel}
                  />
                )}

                {message.role === 'assistant' && visibleDataTables.map((table, i) => (
                  <DataTable key={`${table.toolName || 'table'}-${i}`} table={table} />
                ))}

                {message.role === 'user' && Array.isArray(message.attachments) && message.attachments.length > 0 && (
                  <div className="ai-msg-attachments" aria-label="Attached files">
                    {message.attachments.map((att) => (
                      <span key={att.id} className={`ai-attachment-chip is-sent${att.status === 'failed' ? ' is-failed' : ''}`} title={att.status === 'failed' ? `${att.fileName} — could not be read${att.error ? `: ${att.error}` : ''}` : att.fileName}>
                        <span className="ai-attachment-icon" aria-hidden="true">
                          {att.status === 'uploading' ? <span className="ai-attachment-spinner" /> : '📎'}
                        </span>
                        <span className="ai-attachment-name">{att.fileName}</span>
                        {att.status === 'failed' && <span className="ai-attachment-meta">failed</span>}
                      </span>
                    ))}
                  </div>
                )}

                {message.role === 'assistant' && Array.isArray(message.fileReferences) && message.fileReferences.length > 0 && (
                  <div className="ai-file-references">
                    <span className="ai-file-references-label">Source documents</span>
                    <div className="ai-file-references-list">
                      {message.fileReferences.map((ref) => (
                        <button
                          key={ref.importId}
                          type="button"
                          className="ai-file-reference-card"
                          title={ref.filename || ref.label}
                          onClick={() => openFileReference(ref)}
                        >
                          <span className="ai-file-reference-icon" aria-hidden="true">📄</span>
                          <span className="ai-file-reference-label">{ref.label || ref.filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {message.role === 'assistant' && !isActiveStreamingMsg && (message.content || message.workflowRun) && (
                  <div className="message-actions ai-msg-actions">
                    <button className={`ai-msg-act rating-btn ${message.rating === 'positive' ? 'active' : ''}`} onClick={() => handleRating(index, 'positive')} title="Good response">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                    </button>
                    <button className={`ai-msg-act rating-btn ${message.rating === 'negative' ? 'active' : ''}`} onClick={() => handleRating(index, 'negative')} title="Bad response">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
                    </button>
                    <button className="ai-msg-act" onClick={() => copyMessageToClipboard(message.content)} title="Copy response text">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    </button>
                    <button className="ai-msg-act" onClick={() => retryMessage(index)} disabled={isLoading || isStreaming} title="Regenerate response">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                    </button>
                    {message.webSearchesExecuted > 0 && (
                      <span className="ai-msg-act ai-msg-act-meta" title="Web searches performed">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                        {message.webSearchesExecuted}
                      </span>
                    )}
                    <span className="ai-msg-time">
                      {message.processingTime ? `${(message.processingTime / 1000).toFixed(1)}s · ` : ''}
                      {formatMessageTimestamp(message.timestamp)}
                    </span>
                  </div>
                )}

                {!isActiveStreamingMsg && (
                  <div className="message-timestamp">{formatMessageTimestamp(message.timestamp)}</div>
                )}
              </div>
            </div>
          );
        })
      )}

      {(isLoading || isStreaming) && !messages.find(m => m.id === streamingMessageIdRef.current && m.content) && (
        <div className="ai-chat-message assistant loading has-run-card">
          <div className="message-avatar">
            <LodgeicAIIcon size={16} />
          </div>
          <div className="message-content">
            {/* ALL loading lives inside the working card — rendered
                unconditionally while waiting so no code path can ever show a
                bare orb. Covers the moments before liveTrace is seeded (and
                the non-streaming fallback, which has no SSE events). */}
            <RunCard
              steps={liveTrace?.steps || []}
              status={streamingStatus || ''}
              writing={liveTrace?.phase === 'writing'}
              startedAt={liveTrace?.startedAt || null}
              scopeLabel={runScopeLabel}
              onStop={stopGeneration}
            />
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </>
  );

  // ----- v2 render (non-compact only) -----
  if (!compact) {
    return (
      <div className={`ai-chat-container ai-v2 ${isDarkMode ? 'dark-mode' : ''} ${hasMessages ? 'has-messages' : ''}`}>
        <div className="ai-page">
          {/* No in-panel header — history/share/settings/new-chat live in the
              composer control row next to send. */}
          <div className={'ai-page-body ' + (hasMessages ? 'has-thread' : 'empty')}>
            {hasMessages ? (
              <div
                className="ai-chat-messages"
                ref={messagesContainerRef}
                onScroll={(e) => {
                  if (isStreaming) {
                    const el = e.target;
                    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                    userScrolledUpRef.current = distFromBottom > 80;
                  }
                }}
              >
                {renderMessagesList()}
              </div>
            ) : (
              <WelcomeV2
                onPickPrompt={(p, k) => v2OnPickPrompt(p, k)}
                onOpenPrompts={() => setV2Popover('prompts')}
                highlightPrompts={highlightPrompts}
                hasLocation={v2HasLocation}
              />
            )}
          </div>

          {error && <div className="ai-chat-error">{error}</div>}

          <div
            className={`ai-composer-wrap${isDragOver ? ' is-dragover' : ''}`}
            onDrop={handleComposerDrop}
            onDragOver={handleComposerDragOver}
            onDragLeave={handleComposerDragLeave}
          >
            {renderPendingAttachments()}
            <form className="ai-composer" onSubmit={handleSendMessage}>
              <textarea
                ref={inputRef}
                className="ai-composer-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!isLoading && !isStreaming && canSend) {
                      handleSendMessage(e);
                    }
                  }
                }}
                onPaste={handleComposerPaste}
                placeholder={v2HasLocation ? 'Ask about rates, pace, or the P&L…' : 'Loading properties…'}
                disabled={!v2HasLocation}
                rows={1}
                onInput={(e) => {
                  const el = e.target;
                  el.style.height = '0';
                  el.style.height = Math.min(el.scrollHeight, 160) + 'px';
                  el.style.overflow = el.scrollHeight > 160 ? 'auto' : 'hidden';
                }}
              />
              <div className="ai-composer-controls">
                <button type="button" className="ai-plus-btn" onClick={() => setV2Popover('tools')} title="More tools" aria-label="More tools">
                  <IconPlus size={14} />
                </button>
                {renderAttachmentButton('ai-plus-btn ai-attach-btn')}
                <button type="button" className="ai-chip" onClick={() => setV2Popover('properties')} title="Scope">
                  <IconPin size={12} />
                  <span className="ai-chip-label">{v2PropertyLabel}</span>
                  <IconChevronDown size={11} />
                </button>
                <FastModeToggle
                  enabled={selectedModel === 'fast'}
                  onChange={(enabled) => setSelectedModel(enabled ? 'fast' : 'auto')}
                />
                <div className="ai-composer-spacer" />
                {isGenerating ? (
                  <button
                    type="button"
                    onClick={stopGeneration}
                    className="ai-send-btn is-stop"
                    title="Stop generating"
                    aria-label="Stop generating"
                  >
                    <IconStop size={12} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canSend || !v2HasLocation}
                    className={'ai-send-btn ' + (canSend ? 'is-ready' : '')}
                    title="Send message (Enter)"
                  >
                    <IconArrowUp size={16} />
                  </button>
                )}
              </div>
            </form>
            <div className="ai-composer-foot">
              Lodgeic AI can make mistakes — double-check important figures.
            </div>
          </div>
        </div>

        {/* Popovers */}
        <PropertiesPopover
          open={v2Popover === 'properties'}
          onClose={() => setV2Popover(null)}
          locations={locations}
          selectedIds={selectedLocationIds}
          onToggle={v2TogglePropId}
          onSelectAll={v2SelectAll}
          onClear={v2Clear}
          onApply={v2ApplyProperties}
          query={v2PropQuery}
          setQuery={setV2PropQuery}
        />
        <PromptsPopover
          open={v2Popover === 'prompts'}
          onClose={() => setV2Popover(null)}
          onPick={v2OnPickPrompt}
          highlightPrompts={highlightPrompts}
        />
        <ComposerToolsPopover
          open={v2Popover === 'tools'}
          onClose={() => setV2Popover(null)}
          canShare={Boolean(hasMessages && (activeThreadId || threadId))}
          onPrompts={() => setV2Popover('prompts')}
          onHistory={() => setV2Tray('history')}
          onShare={handleShareSession}
          onSettings={() => setV2Tray('settings')}
          onNewChat={handleNewConversation}
        />

        {/* Trays */}
        <SettingsTray
          open={v2Tray === 'settings'}
          onClose={() => setV2Tray(null)}
          settings={assistantSettings}
          onChange={v2SetSetting}
        />
        <HistoryTray
          open={v2Tray === 'history'}
          onClose={() => setV2Tray(null)}
          sessions={chatSessions}
          onPick={(id) => { if (onSelectSession) onSelectSession(id); }}
          onDelete={onDeleteSession}
          currentThreadId={activeThreadId}
          onNewChat={handleNewConversation}
          isLoading={isLoadingSessions}
        />
        <AutomationsTray
          open={v2Tray === 'automations'}
          onClose={() => setV2Tray(null)}
          currentUser={userSettings}
        />

        {activeFileView && activeFileView.url && (
          <FileViewerModal
            url={activeFileView.url}
            fileType="pdf"
            onClose={closeFileReference}
          />
        )}
      </div>
    );
  }

  // ----- Compact (embedded) mode — preserve existing UI -----
  return (
    <div className={`ai-chat-container ${isDarkMode ? 'dark-mode' : ''} ${hasMessages ? 'has-messages' : ''}`}>
      {/* No header in full mode — everything lives in the + menu */}

      {/* Compact empty state */}
      {!hasMessages && compact && (
        <div className="ai-chat-messages">
          <div className={`ai-chat-empty ${suggestedPrompts?.length ? 'ai-chat-empty--with-prompts' : ''} ${contextPrefix ? 'forecast-ctx' : ''}`}>
            {contextPrefix ? (
              <>
                <div className="forecast-ctx-hub">
                  <div className="forecast-ctx-hub-ring" />
                  <div className="forecast-ctx-hub-icon">
                    <LodgeicAIIcon size={compact ? 26 : 32} withBackground={false} />
                  </div>
                </div>
                <h4>How can I help with this forecast?</h4>
                <p>Ask questions, analyze variances, or let AI suggest changes.</p>
              </>
            ) : (
              <>
                <div className="ai-chat-empty-icon-wrapper">
                  <LodgeicAIIcon size={40} withBackground={false} />
                </div>
                <h4>How can I help you today?</h4>
                <p>Ask questions about your property data, forecasts, revenue trends, and more.</p>
              </>
            )}
            {suggestedPrompts?.length > 0 && (
              <div className="suggested-prompt-cards">
                {suggestedPrompts.map((sp, i) => (
                  <button
                    key={i}
                    className="suggested-prompt-card"
                    onClick={() => handlePromptClick(sp.prompt, sp.promptKey || null, sp.workflowRef ? { workflowRef: sp.workflowRef, label: sp.title, workflowContext: sp.workflowContext } : null)}
                    disabled={isLoading}
                  >
                    <span className="suggested-prompt-icon">{sp.icon}</span>
                    <div className="suggested-prompt-text">
                      <strong>
                        {sp.title}
                        {sp.isNew && <span className="suggested-prompt-new">NEW</span>}
                      </strong>
                      <span>{sp.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages area */}
      {hasMessages && (
        <div
          className="ai-chat-messages"
          ref={messagesContainerRef}
          onScroll={(e) => {
            if (isStreaming) {
              const el = e.target;
              const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
              userScrolledUpRef.current = distFromBottom > 80;
            }
          }}
        >
          {renderMessagesList()}
        </div>
      )}

      {error && (
        <div className="ai-chat-error">
          {error}
        </div>
      )}

      {compact && (
        <div className="ai-chat-compact-toolbar">
          <FastModeToggle
            enabled={selectedModel === 'fast'}
            onChange={(enabled) => setSelectedModel(enabled ? 'fast' : 'auto')}
          />
        </div>
      )}

      {/* Bottom input form — compact mode */}
      {(hasMessages || compact) && renderInputForm()}

    </div>
  );
};

export default AIChat;
